// MobileApp.jsx — Phase 1 mobile layout for Tour Managers
// Same data as desktop; adapted layout for mobile viewport.
// Crew/band views come in Phase 3.

import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { supabase } from "./supabase.js";
import { TOK } from "./tokens.js";
import { EmptyState } from "./ui.jsx";
import { fmtPhone } from "./tokens.js";
import ProfileEditor, { Switch } from "./ProfileEditor.jsx";
import ChatPanel from "./ChatPanel.jsx";

const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

// ── Role-category accent palette ────────────────────────────────────────────
// Single source of truth for the network/profile role colours (was duplicated
// inline in TodayScreen, DayDetailOverlay, MemberProfileScreen, ProfileDetailOverlay).
const CAT_COLORS = {
  band: "#b09cbf", crew: "#8faabc", tm: "#80aaa8", management: "#c4a882",
  production: "#9db89d", session: "#bc8f8f", other: "#9a9490",
};
const roleColor = (cat, fallback) => CAT_COLORS[cat] || fallback;

// ── Skeleton loader ─────────────────────────────────────────────────────────
// Shimmer placeholder used while a day's logistics/weather are fetching, instead
// of left-aligned grey "Loading…" text. `skeletonShimmer` keyframe is injected
// in App.jsx's <style> block.
function Skeleton({ w = "100%", h = 12, UI, style }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: 4,
      background: `linear-gradient(90deg, ${UI.panelAlt} 25%, ${UI.rowHov} 37%, ${UI.panelAlt} 63%)`,
      backgroundSize: "400% 100%",
      animation: "skeletonShimmer 1.4s ease infinite",
      ...style,
    }} />
  );
}
function SkeletonRows({ UI, lines = 2 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Skeleton UI={UI} w="55%" h={14} />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} UI={UI} w={i === lines - 1 ? "40%" : "75%"} h={11} />
      ))}
    </div>
  );
}

// ── Shared inline icons (Rule 0: inline SVG, never glyph/emoji characters) ──
function ChevronIcon({ dir = "right", size = 13, style }) {
  const rot = dir === "left" ? "rotate(180deg)" : dir === "down" ? "rotate(90deg)" : dir === "up" ? "rotate(-90deg)" : "none";
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", transform: rot, ...style }}>
      <path d="M4.5 2l4 4-4 4"/>
    </svg>
  );
}
function WxIcon({ condition, size = 20 }) {
  const p = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", width: size, height: size, style: { display: "block" } };
  if (condition === "Rain")   return <svg {...p}><path d="M20 16.6A5 5 0 0 0 18 7h-1.3A8 8 0 1 0 4 15.3"/><line x1="8" y1="19" x2="8" y2="21"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="16" y1="19" x2="16" y2="21"/></svg>;
  if (condition === "Snow")   return <svg {...p}><path d="M20 16.6A5 5 0 0 0 18 7h-1.3A8 8 0 1 0 4 15.3"/><line x1="8" y1="17" x2="8.01" y2="17"/><line x1="8" y1="21" x2="8.01" y2="21"/><line x1="12" y1="19" x2="12.01" y2="19"/><line x1="16" y1="17" x2="16.01" y2="17"/><line x1="16" y1="21" x2="16.01" y2="21"/></svg>;
  if (condition === "Clouds") return <svg {...p}><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>;
  if (condition === "Clear")  return <svg {...p}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>;
  // Default: sun behind cloud
  return <svg {...p}><path d="M12 2v2M4.93 4.93l1.41 1.41M20 12h2M19.07 4.93l-1.41 1.41M15.947 12.65a4 4 0 0 0-5.925-4.128"/><path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6z"/></svg>;
}

// ── Empty state ─────────────────────────────────────────────────────────────
// One consistent mobile empty pattern (was hand-rolled with —/◻/⬡ at varying
// sizes/colours across every screen). glyph defaults to an em-dash.
function MobileEmpty({ UI, title, hint, glyph = "—", pad = "40px 20px" }) {
  return (
    <div style={{ padding: pad, textAlign: "center" }}>
      <div style={{ fontFamily: FF, fontSize: 32, color: UI.textVFnt, marginBottom: 10, opacity: 0.55, lineHeight: 1 }}>{glyph}</div>
      <div style={{ fontFamily: FF, fontSize: 14, color: UI.textMut }}>{title}</div>
      {hint && (
        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 5, lineHeight: 1.6, maxWidth: 260, marginLeft: "auto", marginRight: "auto" }}>{hint}</div>
      )}
    </div>
  );
}

// ── Who's-in-Town person row ────────────────────────────────────────────────
// Shared by TodayScreen and DayDetailOverlay (was two ~95%-identical copies).
// `compact` = the slightly smaller Day-Detail variant. `showTeam` adds the
// team name to the sub-label (Today shows it; Day Detail doesn't).
function NetworkPersonRow({ p, isLast, city, myName, connected, onOpen, onToggleConnect, UI, compact = false, showTeam = false }) {
  const roleLabel  = p.role || p.role_category || "";
  const teamLabel  = showTeam ? (p.teams?.name || "") : "";
  const cleanPhone = (p.phone || "").replace(/[^+\d]/g, "");
  const recipName  = p.preferred_name || p.name || "there";
  const pingBody   = encodeURIComponent(`Hey ${recipName}${myName ? ` — ${myName} here` : ""}. Looks like we're both in ${city || "the same city"} today. Want to link up?`);
  const catColor   = roleColor(p.role_category, UI.textFnt);
  const initials   = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const subLabel   = [roleLabel, teamLabel].filter(Boolean).join(" · ");
  const av         = compact ? 34 : 38;
  const nameSize   = compact ? 12 : 14;
  const btnPad     = compact ? "4px 8px" : "5px 9px";
  const pingSvg    = compact ? 10 : 11;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      paddingBottom: isLast ? 0 : 12, marginBottom: isLast ? 0 : 12,
      borderBottom: isLast ? "none" : `1px solid ${UI.panelBdr}`,
    }}>
      {/* Tappable left section — opens full profile overlay */}
      <button onClick={() => onOpen(p)}
        style={{ display: "flex", alignItems: "center", gap: compact ? 10 : 12, flex: 1, minWidth: 0, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", WebkitTapHighlightColor: "transparent" }}>
        <div style={{
          width: av, height: av, borderRadius: "50%", flexShrink: 0,
          background: `${catColor}18`, border: `2px solid ${catColor}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: FF, fontSize: 14, fontWeight: 700, color: catColor, overflow: "hidden",
        }}>
          {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontFamily: FF, fontSize: nameSize, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {p.preferred_name || p.name}
            </span>
            {connected && (
              <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "0.8px", color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "1px 4px", flexShrink: 0 }}>CONNECTED</span>
            )}
          </div>
          {subLabel && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subLabel}</div>
          )}
        </div>
      </button>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: compact ? 5 : 6, flexShrink: 0 }}>
        {cleanPhone && (
          <a href={`sms:${cleanPhone}?&body=${pingBody}`}
            style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: "0.6px", padding: btnPad, borderRadius: 4, background: `${UI.green}18`, border: `1px solid ${UI.green}44`, color: UI.green, textDecoration: "none", display: "flex", alignItems: "center", gap: compact ? 3 : 4, WebkitTapHighlightColor: "transparent" }}>
            <svg width={pingSvg} height={pingSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Ping
          </a>
        )}
        <button onClick={() => onToggleConnect?.(p.id)}
          style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: "0.8px", padding: btnPad, borderRadius: 4, cursor: "pointer", background: connected ? UI.amdBg : UI.panelAlt, border: `1px solid ${connected ? UI.amdBdr : UI.panelBdr}`, color: connected ? UI.amber : UI.textMut, transition: "all 0.12s", WebkitTapHighlightColor: "transparent" }}>
          {connected ? "Following" : "Connect"}
        </button>
      </div>
    </div>
  );
}

// ── Nav Icons (SVG) ───────────────────────────────────────────────────────────
function NavIcon({ id, color }) {
  const s = { width: 20, height: 20, viewBox: "0 0 20 20", fill: "none", stroke: color, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" };
  if (id === "today") return (
    <svg {...s}>
      <circle cx="10" cy="10" r="3.5"/>
      <line x1="10" y1="1.5" x2="10" y2="4"/>
      <line x1="10" y1="16" x2="10" y2="18.5"/>
      <line x1="1.5" y1="10" x2="4" y2="10"/>
      <line x1="16" y1="10" x2="18.5" y2="10"/>
      <line x1="3.7" y1="3.7" x2="5.6" y2="5.6"/>
      <line x1="14.4" y1="14.4" x2="16.3" y2="16.3"/>
      <line x1="16.3" y1="3.7" x2="14.4" y2="5.6"/>
      <line x1="5.6" y1="14.4" x2="3.7" y2="16.3"/>
    </svg>
  );
  if (id === "shows") return (
    <svg {...s}>
      <rect x="7" y="2" width="6" height="9" rx="3"/>
      <path d="M4 10a6 6 0 0 0 12 0"/>
      <line x1="10" y1="16" x2="10" y2="18.5"/>
      <line x1="7" y1="18.5" x2="13" y2="18.5"/>
    </svg>
  );
  if (id === "tour") return (
    <svg {...s}>
      <rect x="2" y="4" width="16" height="14" rx="2"/>
      <line x1="2" y1="8.5" x2="18" y2="8.5"/>
      <line x1="6" y1="2" x2="6" y2="6"/>
      <line x1="14" y1="2" x2="14" y2="6"/>
      <line x1="6"  y1="13" x2="6"  y2="13" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="10" y1="13" x2="10" y2="13" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="14" y1="13" x2="14" y2="13" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
  if (id === "people") return (
    <svg {...s}>
      <circle cx="10" cy="6.5" r="3"/>
      <path d="M3.5 18c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5"/>
    </svg>
  );
  if (id === "schedule") return (
    <svg {...s}>
      <rect x="2" y="3" width="16" height="15" rx="2"/>
      <line x1="2" y1="7.5" x2="18" y2="7.5"/>
      <line x1="6" y1="1.5" x2="6" y2="5"/>
      <line x1="14" y1="1.5" x2="14" y2="5"/>
      <line x1="6"  y1="11.5" x2="14" y2="11.5"/>
      <line x1="6"  y1="15"   x2="10" y2="15"/>
    </svg>
  );
  if (id === "more") return (
    <svg {...s} stroke="none">
      <circle cx="5" cy="10" r="1.5" fill={color}/>
      <circle cx="10" cy="10" r="1.5" fill={color}/>
      <circle cx="15" cy="10" r="1.5" fill={color}/>
    </svg>
  );
  return null;
}

// ── Bottom Navigation ─────────────────────────────────────────────────────────
function BottomNav({ tab, onTab, UI, memberRole }) {
  const tabs = [
    { id: "today",    label: "Today"    },
    { id: "tour",     label: "Tour"     },
    { id: "schedule", label: "Schedule" },
    { id: "people",   label: memberRole ? "Profile" : "People" },
    { id: "more",     label: "More"     },
  ];
  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 450,
      background: UI.bg, borderTop: `1px solid ${UI.panelBdr}`,
      display: "flex",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    }}>
      {tabs.map(t => {
        const active = tab === t.id;
        const color  = active ? UI.amber : UI.textFnt;
        return (
          <button key={t.id} onClick={() => onTab(t.id)}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", padding: "10px 0 8px",
              background: "none", border: "none", cursor: "pointer", gap: 4,
              WebkitTapHighlightColor: "transparent",
            }}>
            <NavIcon id={t.id} color={color} />
            <span style={{
              fontFamily: FF, fontSize: 11, letterSpacing: "0.5px",
              color, fontWeight: active ? 700 : 400,
            }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function SectionLabel({ children, UI }) {
  return (
    <div style={{
      fontFamily: FF, fontSize: 11, letterSpacing: "2px",
      textTransform: "uppercase", color: UI.textFnt, marginBottom: 10,
    }}>{children}</div>
  );
}

// Small inline SVG icons for schedule row labels
const SCHEDULE_ICONS = {
  intermission: (color) => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill={color} style={{ marginRight: 4, flexShrink: 0, verticalAlign: "middle" }}>
      <rect x="1.5" y="1" width="2.5" height="8" rx="0.5"/>
      <rect x="6"   y="1" width="2.5" height="8" rx="0.5"/>
    </svg>
  ),
  changeover: (color) => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, flexShrink: 0, verticalAlign: "middle" }}>
      <polyline points="2,5 8,5 6,3"/>
      <polyline points="6,7 8,5"/>
    </svg>
  ),
};

function InfoRow({ label, value, href, UI, icon }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 }}>
      <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, display: "flex", alignItems: "center" }}>
        {icon && SCHEDULE_ICONS[icon]?.(UI.textMut)}
        {label}
      </span>
      {href ? (
        <a href={href} style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.amber, textDecoration: "none" }}>{value}</a>
      ) : (
        <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.text }}>{value}</span>
      )}
    </div>
  );
}

// Left-aligned schedule row: time in a fixed left column, label to the right
function SchedRow({ label, value, UI, icon }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 8 }}>
      <div style={{ flexShrink: 0, width: 68, fontFamily: FF, fontSize: 12, fontWeight: 700, color: UI.text }}>
        {value}
      </div>
      <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut, display: "flex", alignItems: "center", gap: 4 }}>
        {icon && SCHEDULE_ICONS[icon]?.(UI.textMut)}
        {label}
      </div>
    </div>
  );
}

function Section({ label, children, UI }) {
  return (
    <div style={{ padding: "14px 20px", borderBottom: `1px solid ${UI.panelBdr}` }}>
      <SectionLabel UI={UI}>{label}</SectionLabel>
      {children}
    </div>
  );
}

// Amber-banner section wrapper for advance detail categories
function AdvSection({ label, children, UI }) {
  return (
    <div style={{ borderBottom: `1px solid ${UI.panelBdr}` }}>
      <div style={{
        padding: "8px 20px",
        background: `${UI.amber}18`,
        borderBottom: `1px solid ${UI.amber}28`,
        fontFamily: FF, fontSize: 11, letterSpacing: "2px",
        textTransform: "uppercase", color: UI.amber, fontWeight: 700,
      }}>
        {label}
      </div>
      <div style={{ padding: "14px 20px 6px" }}>
        {children}
      </div>
    </div>
  );
}

// Sub-title + detail item within an AdvSection
function AdvInfoItem({ label, value, UI }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textMut, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 500, color: UI.text, lineHeight: 1.55 }}>
        {value}
      </div>
    </div>
  );
}

// ── Time helpers ──────────────────────────────────────────────────────────────
// Convert "HH:MM" 24h string → "H:MM AM/PM". Returns as-is if already has AM/PM or unrecognised.
function fmtTime12(t) {
  if (!t || typeof t !== "string") return t;
  if (/[AP]M$/i.test(t.trim())) return t.trim(); // already formatted
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${suffix}`;
}

// Convert yes/no advance field value to display string.
// Portal saves booleans (true/false); older data may have "yes"/"no" strings.
function fmtYN(v) {
  if (v === true  || v === "yes") return "Yes";
  if (v === false || v === "no")  return "No";
  return null;
}
// Returns true if a yesno field has been explicitly set (including to false/No)
const isYNSet = (v) => v === true || v === false || v === "yes" || v === "no";

// Parse a time string (HH:MM or H:MM AM/PM) to minutes for sorting.
// Times before 6 AM (< 360 min) are treated as next-day by adding 1440 so that
// midnight curfews (12:00 AM) sort AFTER late-evening events, not before noon.
function timeToMins(t) {
  if (!t || typeof t !== "string") return 9999;
  let raw = 9999;
  const m12 = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const mn = parseInt(m12[2], 10);
    const pm = m12[3].toUpperCase() === "PM";
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    raw = h * 60 + mn;
  } else {
    const m24 = t.match(/^(\d{1,2}):(\d{2})/);
    if (m24) raw = parseInt(m24[1], 10) * 60 + parseInt(m24[2], 10);
  }
  if (raw >= 9999) return 9999;
  // Wrap early-morning times to next-day so they sort after PM events
  return raw < 360 ? raw + 1440 : raw;
}
// Compute set duration in minutes from two display time strings; handles midnight wrap
function setDurMins(startStr, endStr) {
  const s = timeToMins(startStr), e = timeToMins(endStr);
  if (s >= 9999 || e >= 9999) return null;
  let d = e - s;
  if (d <= 0) d += 24 * 60;
  return d;
}
function fmtDur(mins) {
  if (!mins || mins <= 0) return "";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// ── Today Screen ──────────────────────────────────────────────────────────────
function TodayScreen({ tourDays, tour, venues, dayWeather, memberRole, memberModules, lightMode, UI,
  dayLodging, dayFlights, dayTransport, loadDayLodging, loadDayFlights, loadDayTransport, loadDayWeather,
  memberOwnProfile, memberNetworkProfiles, memberConnections, toggleMemberConnection, onGoToProfile,
  cityRecs, loadCommunityRecs, dayScheduleItems,
  showReports, setShowReportModal, reportDismissedShows, dismissReport,
  broadcasts = [], broadcastReads = {}, markBroadcastRead, currentUserId }) {
  const today = new Date().toISOString().slice(0, 10);

  const defaultIdx = (() => {
    const todayIdx = tourDays.findIndex(d => d.date === today);
    if (todayIdx !== -1) return todayIdx;
    const nextIdx = tourDays.findIndex(d => d.date > today);
    if (nextIdx !== -1) return nextIdx;
    return tourDays.length - 1;
  })();

  const [dayIdx, setDayIdx]                   = useState(defaultIdx < 0 ? 0 : defaultIdx);
  const [nudgeDismissed, setNudgeDismissed]   = useState(false);
  const [welcomeMsg,     setWelcomeMsg]       = useState(null);
  const [welcomeLoading, setWelcomeLoading]   = useState(false);
  const [inTownOverlay,  setInTownOverlay]    = useState(null);  // profile to show in who's-in-town detail overlay
  const day = tourDays[dayIdx];

  // NOTE: all hooks must run unconditionally on every render (Rules of Hooks +
  // Rolldown's strict enforcement). The "no tour days" early return therefore
  // lives AFTER the useEffects below — not here. Derived values use optional
  // chaining so they don't throw when `day` is undefined.
  const showObj  = day?.show_id ? (tour?.shows || []).find(s => s.id === day.show_id) : null;
  const venue    = showObj ? venues[showObj.venueId] : null;
  const city     = day?.city || venue?.city || "";
  const wx       = dayWeather?.[day?.id];
  const isOff    = day?.type !== "show";
  const isToday  = day?.date === today;

  // Lazy-load hotel/flights/transport/weather + community recs for the currently visible day
  useEffect(() => {
    if (!day?.id) return;
    loadDayLodging?.(day.id);
    loadDayFlights?.(day.id);
    loadDayTransport?.(day.id);
    const wxCity = (day.type === "show" && venue?.city) ? venue.city : (day.city || "");
    if (wxCity) loadDayWeather?.(day.id, wxCity, day.date);
    const recCity = wxCity || city;
    const recState = venue?.addressState || day.state || "";
    if (recCity) loadCommunityRecs?.(recCity, recState);
  }, [day?.id]);

  // Welcome note — AI-generated, cached per day in sessionStorage
  useEffect(() => {
    if (!day?.date) return;
    const welcomeMod = (memberModules || []).find(m => m.type === "welcome");
    // Only generate if module is enabled (or no config yet — default on)
    const isEnabled = !memberModules?.length || (welcomeMod ? welcomeMod.enabled : true);
    if (!isEnabled) return;

    // Include a schedule-availability flag in cache key so the message regenerates
    // once day_schedule_items have loaded (avoiding stale messages with invented times)
    const hasSchedData = (dayScheduleItems || []).some(x => x.tour_day_id === day.id);
    const cacheKey = `adv_welcome_${day.date}_${hasSchedData ? "s1" : "s0"}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) { setWelcomeMsg(cached); return; }
    } catch {}

    const firstName = (memberOwnProfile?.preferred_name || memberOwnProfile?.name || "").split(" ")[0].trim();
    const msgCity   = (day.type === "show" && venue?.city) ? venue.city : (day.city || "");
    const venueName = venue?.name || null;
    if (!firstName && !msgCity) return; // not enough context to personalise

    const dayType = day.type === "show"
      ? (venueName ? `show day at ${venueName}` : "show day")
      : day.type === "travel" ? "travel day" : "day off";
    const dateStr = new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

    // Pull the load-in / venue access time from curated day_schedule_items if available
    const todaySchedItems = (dayScheduleItems || []).filter(x => x.tour_day_id === day.id);
    const loadInItem = todaySchedItems.find(x =>
      x.source_key === "adv_day_venue_access" ||
      x.title?.toLowerCase().includes("venue access") ||
      x.title?.toLowerCase().includes("load-in") ||
      x.title?.toLowerCase().includes("load in")
    );
    const fmtSchedTime = (mins) => {
      if (!mins && mins !== 0) return null;
      const h = Math.floor(mins / 60) % 24;
      const m = mins % 60;
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
    };
    const loadInTime = loadInItem ? fmtSchedTime(loadInItem.start_minutes) : null;

    const prompt = `You write the daily opener for a touring musician's briefing app. You sound like a trusted tour manager — direct, warm, zero hype. You speak plainly. You never oversell a day.

Context:
${firstName ? `Name: ${firstName}` : ""}
Date: ${dateStr}
${msgCity ? `City: ${msgCity}` : ""}
Day: ${dayType}${loadInTime ? `\nLoad-in: ${loadInTime}` : ""}

Write 1–2 sentences. Output only the message, no quotes.

Hard rules — violating any of these makes the message unusable:
- No exclamation marks
- Never use: amazing, incredible, exciting, epic, crushing it, killing it, pumped, stoked, can't wait, big day, great day
- Don't open with a hollow compliment or hype ("What a day", "Today is going to be special")
- If it's a day off, acknowledge it as a day off — don't frame rest as secretly exciting
- If it's a travel day, be honest that it's a travel day
- Vary the sentence structure — don't always lead with the name or the city
- Do NOT mention or invent any specific times unless they are explicitly listed in Context above
- Do NOT draw on any knowledge of the venue's typical schedule or past shows

Aim for the kind of note a thoughtful person actually writes, not the kind an app generates.`;

    setWelcomeLoading(true);
    setWelcomeMsg(null);

    fetch("/api/anthropic", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 100,
        messages: [{ role: "user", content: prompt }],
      }),
    })
      .then(r => r.json())
      .then(d => {
        const msg = d.content?.[0]?.text?.trim();
        if (msg) {
          setWelcomeMsg(msg);
          try { sessionStorage.setItem(cacheKey, msg); } catch {}
        }
      })
      .catch(() => {})
      .finally(() => setWelcomeLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day?.date, (dayScheduleItems || []).some(x => x.tour_day_id === day?.id)]);

  // Who's-in-town: filter the network down to people routing through this city
  // today. Memoized — the .filter + nested .some over every network profile's
  // routing was running on every render. Computed before the early return.
  const inTownToday = useMemo(() => {
    const dayCity = (city || "").toLowerCase().trim();
    if (!dayCity || !memberNetworkProfiles?.length || !day?.date) return [];
    return memberNetworkProfiles.filter(p =>
      Array.isArray(p.tour_routing) && p.tour_routing.some(r => {
        const rCity = (r.city || "").toLowerCase().trim();
        return r.date === day.date && (rCity === dayCity || rCity.startsWith(dayCity) || dayCity.startsWith(rCity));
      })
    );
  }, [memberNetworkProfiles, city, day?.date]);

  // No tour days yet — safe to return now that every hook above has run
  // unconditionally, so React's hook order stays stable across renders.
  if (!day) {
    return (
      <div style={{ flex: 1, display: "flex" }}>
        <EmptyState
          title="No tour days yet"
          hint="Set up tour mode on desktop first."
          style={{ flex: 1, justifyContent: "center" }}
        />
      </div>
    );
  }

  // Resolved data for current day
  // undefined = not yet fetched (show skeleton); array = loaded (may be empty)
  const lodging   = dayLodging?.[day?.id];
  const flights   = dayFlights?.[day?.id];
  const transport = dayTransport?.[day?.id];
  const lodgingLoading  = lodging   === undefined;
  const flightsLoading  = flights   === undefined;
  const transportLoading = transport === undefined;
  const lodgingList   = lodging   || [];
  const flightsList   = flights   || [];
  const transportList = transport || [];

  const dateLabel = new Date(day.date + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });

  // Module visibility — TMs see everything; band/crew see only enabled modules in configured order
  const modEnabled = (type) => {
    if (!memberRole) return true; // TM: always show
    if (!memberModules?.length) return true; // no config yet: show all
    const mod = memberModules.find(m => m.type === type);
    return mod ? mod.enabled : true;
  };

  // Schedule sub-field visibility
  const schedFieldEnabled = (fieldId) => {
    if (!memberRole) return true;
    const schedMod = memberModules?.find(m => m.type === "schedule");
    if (!schedMod?.config || Object.keys(schedMod.config).length === 0) return true;
    return schedMod.config[fieldId] !== false;
  };

  // Rich schedule data from advanceTmData (filled by TM in advance portal)
  const sch = showObj?.advanceTmData?.schedule || {};


  return (
    <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80, WebkitOverflowScrolling: "touch" }}>

      {/* Day header with prev/next */}
      <div style={{
        padding: "16px 20px 14px", borderBottom: `1px solid ${UI.panelBdr}`,
        display: "flex", alignItems: "center", gap: 8,
        background: isToday ? (lightMode ? "#fffdf5" : "#0f0e08") : UI.bg,
      }}>
        <button onClick={() => setDayIdx(i => Math.max(0, i - 1))} disabled={dayIdx === 0} aria-label="Previous day"
          style={{ background: "none", border: "none", color: dayIdx === 0 ? UI.panelBdr : UI.textMut, cursor: "pointer", padding: "10px 12px", margin: "-6px 0", lineHeight: 1, WebkitTapHighlightColor: "transparent" }}><ChevronIcon dir="left" size={16} /></button>

        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: isToday ? UI.amber : UI.textFnt, marginBottom: 3 }}>
            {isToday ? "Today · " : ""}{dateLabel}
            {!isOff && (
              <span style={{
                marginLeft: 6, fontSize: 11, letterSpacing: "1.5px",
                color: UI.amber, background: UI.amdBg,
                padding: "1px 6px", borderRadius: 4,
              }}>SHOW</span>
            )}
          </div>
          <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text, lineHeight: 1.1 }}>
            {venue?.name || city || (isOff ? "Day Off" : "Show Day")}
          </div>
          {venue?.name && city && city !== venue.name && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 3 }}>{city}</div>
          )}
          {isOff && city && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 3 }}>{city}</div>
          )}
        </div>

        <button onClick={() => setDayIdx(i => Math.min(tourDays.length - 1, i + 1))} disabled={dayIdx === tourDays.length - 1} aria-label="Next day"
          style={{ background: "none", border: "none", color: dayIdx === tourDays.length - 1 ? UI.panelBdr : UI.textMut, cursor: "pointer", padding: "10px 12px", margin: "-6px 0", lineHeight: 1, WebkitTapHighlightColor: "transparent" }}><ChevronIcon size={16} /></button>
      </div>

      {/* COMM-1: Unread broadcasts card */}
      {(() => {
        const unread = broadcasts.filter(b => {
          const reads = broadcastReads[b.id];
          return !reads || !reads.has(currentUserId);
        });
        if (!unread.length) return null;
        const todayStr = new Date().toISOString().slice(0, 10);
        const fmtTs = (iso) => {
          const d = new Date(iso);
          return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " +
                 d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
        };
        const fmtDayLabel = (dayId) => {
          const day = tourDays.find(d => d.id === dayId);
          if (!day) return null;
          const dateStr = new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
          const city = day.city || day.venue_city || "";
          return city ? `${dateStr} · ${city}` : dateStr;
        };
        const isDayPast = (dayId) => {
          const day = tourDays.find(d => d.id === dayId);
          return day ? day.date < todayStr : false;
        };
        return (
          <div style={{ margin: "12px 16px 4px", padding: "12px 14px", background: UI.amber + "18", border: `1.5px solid ${UI.amber}55`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: UI.amber, flexShrink: 0 }} />
              <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", color: UI.amber, fontWeight: 700, textTransform: "uppercase" }}>
                Update{unread.length > 1 ? "s" : ""} from Tour Manager
              </span>
              {unread.length > 1 && (
                <span style={{ fontFamily: FF, fontSize: 11, background: UI.amber, color: "var(--accent-fg)", borderRadius: 8, padding: "1px 7px", fontWeight: 700, marginLeft: 2 }}>{unread.length}</span>
              )}
            </div>
            {unread.slice(0, 3).map((b, i) => {
              const past = b.tour_day_id ? isDayPast(b.tour_day_id) : false;
              const dayLabel = b.tour_day_id ? fmtDayLabel(b.tour_day_id) : null;
              return (
                <div key={b.id} style={{
                  paddingTop: i > 0 ? 10 : 0,
                  borderTop: i > 0 ? `1px solid ${UI.amber}30` : "none",
                  opacity: past ? 0.6 : 1,
                }}>
                  {/* Day context header */}
                  {dayLabel && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                      <span style={{
                        fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: "1.2px",
                        textTransform: "uppercase",
                        color: past ? UI.textMut : UI.amber,
                      }}>
                        {dayLabel}
                      </span>
                      {past && (
                        <span style={{
                          fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: "1px",
                          textTransform: "uppercase", color: UI.textMut,
                          background: lightMode ? "#0001" : "#fff2",
                          border: `1px solid ${UI.textMut}55`,
                          borderRadius: 4, padding: "1px 5px",
                        }}>PAST</span>
                      )}
                    </div>
                  )}
                  <div style={{ fontFamily: FF, fontSize: 14, color: UI.text, lineHeight: 1.5, marginBottom: 6 }}>{b.body}</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{fmtTs(b.created_at)}</span>
                    <button
                      onClick={() => markBroadcastRead?.(b.id)}
                      style={{ fontFamily: FF, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amber}66`, borderRadius: 4, padding: "7px 14px", cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                      Got it
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Onboarding nudge — shown to members who haven't set up their profile yet */}
      {memberRole && !memberOwnProfile && !nudgeDismissed && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "11px 16px 11px 20px",
          background: lightMode ? "#fffbee" : "#161208",
          borderBottom: `1px solid ${lightMode ? "#f0c84a33" : "#f0a50033"}`,
        }}>
          <span style={{ flexShrink: 0, opacity: 0.7 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.amber, lineHeight: 1.3 }}>
              Set up your profile
            </div>
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>
              Appear in "who's in town" for others on the road
            </div>
          </div>
          <button
            onClick={() => onGoToProfile?.()}
            style={{
              fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: "0.8px",
              padding: "5px 10px", borderRadius: 4, cursor: "pointer", flexShrink: 0,
              background: UI.amber, color: "var(--accent-fg)", border: "none",
              WebkitTapHighlightColor: "transparent",
            }}>
            Go →
          </button>
          <button
            onClick={() => setNudgeDismissed(true)}
            style={{
              background: "none", border: "none", color: UI.textVFnt,
              cursor: "pointer", lineHeight: 1, padding: "0 2px", flexShrink: 0,
              WebkitTapHighlightColor: "transparent", display: "inline-flex", alignItems: "center",
            }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        </div>
      )}

      {/* SR-1: Post-show report prompt */}
      {memberRole && showObj && setShowReportModal && (() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const showPast = showObj.date < todayStr;
        const roleCat  = memberOwnProfile?.role_category || memberRole;
        const canReport = roleCat === 'band' || roleCat === 'crew';
        const alreadyDone = (showReports || []).some(r => r.show_id === showObj.id && r.profile_id === memberOwnProfile?.id);
        const dismissed = (reportDismissedShows || []).includes(showObj.id);
        if (!showPast || !canReport || alreadyDone || dismissed) return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 16px 11px 20px",
            background: lightMode ? "#f5f3ff" : "#14121a",
            borderBottom: `1px solid ${lightMode ? "#a78bfa44" : "#7c3aed33"}` }}>
            <span style={{ flexShrink: 0, color: "#7c3aed" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: "#7c3aed", lineHeight: 1.3 }}>How was the show?</div>
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>Submit your post-show report</div>
            </div>
            <button onClick={() => setShowReportModal({ show: showObj, tourId: tour?.id, profileId: memberOwnProfile?.id, roleCategory: roleCat, existingReport: null })}
              style={{ fontFamily: FF, fontSize: 11, padding: "6px 12px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer", flexShrink: 0, fontWeight: 600, WebkitTapHighlightColor: "transparent" }}>
              Report →
            </button>
            <button onClick={() => dismissReport?.(showObj.id)} style={{ color: UI.textVFnt, background: "none", border: "none", cursor: "pointer", padding: "4px", flexShrink: 0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        );
      })()}

      {/* Module sections — rendered in configured order for members, default order for TMs */}
      {(() => {
        const DEFAULT_ORDER = ["welcome", "bus_call", "schedule", "hotel", "flights", "weather", "transport", "notes"];
        const orderedTypes = memberRole && memberModules?.length
          ? [...memberModules].sort((a, b) => (a.order ?? 99) - (b.order ?? 99)).map(m => m.type)
          : DEFAULT_ORDER;

        const renderModule = (type) => {
          switch (type) {

            case "welcome": {
              if (!modEnabled("welcome")) return null;
              if (!welcomeLoading && !welcomeMsg) return null;
              const firstName = (memberOwnProfile?.preferred_name || memberOwnProfile?.name || "").split(" ")[0].trim();
              const greetCity = (day.type === "show" && venue?.city) ? venue.city : (day.city || "");
              return (
                <div key="welcome" style={{
                  padding: "16px 20px",
                  borderBottom: `1px solid ${UI.panelBdr}`,
                  background: lightMode ? "#fffef8" : "#0c0c08",
                }}>
                  {welcomeLoading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: UI.amber, opacity: 0.5, animation: "pulse 1.4s ease-in-out infinite" }} />
                      <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.3px" }}>
                        {firstName ? `Personalising your briefing, ${firstName}…` : "Personalising your briefing…"}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.amber, marginBottom: 7 }}>
                        {greetCity ? `${greetCity} · ` : ""}{new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                      </div>
                      <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: 14, color: UI.text, lineHeight: 1.65, fontStyle: "italic" }}>
                        {welcomeMsg}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            case "bus_call":
              return day.bus_call && modEnabled("bus_call") ? (
                <div key="bus_call" style={{
                  padding: "14px 20px", borderBottom: `1px solid ${UI.panelBdr}`,
                  display: "flex", alignItems: "center", gap: 14,
                  background: lightMode ? "#fffbee" : "#100f08",
                }}>
                  <span style={{ color: UI.amber, display: "flex" }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="13" rx="2"/><path d="M1 11h22M5 17v2M19 17v2"/><circle cx="6" cy="17" r="1" fill="currentColor"/><circle cx="18" cy="17" r="1" fill="currentColor"/></svg></span>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.amber, marginBottom: 3 }}>Bus Call</div>
                    <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text, letterSpacing: "-0.5px" }}>{day.bus_call}</div>
                  </div>
                </div>
              ) : null;

            case "schedule":
              // Off day — show a "no show today" placeholder in the schedule slot for members
              if (!showObj && isOff && memberRole && modEnabled("schedule")) {
                return (
                  <div key="schedule" style={{
                    padding: "14px 20px", borderBottom: `1px solid ${UI.panelBdr}`,
                    display: "flex", alignItems: "center", gap: 14,
                    opacity: 0.7,
                  }}>
                    <span style={{ fontSize: 20, opacity: 0.4 }}>◻</span>
                    <div>
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 2 }}>Today</div>
                      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.textMut }}>
                        {day.type === "travel" ? "Travel day" : `Day off${city ? ` · ${city}` : ""}`}
                      </div>
                    </div>
                  </div>
                );
              }
              if (!showObj || !modEnabled("schedule")) return null;
              const myPid = memberOwnProfile?.id;
              const todayItems = (dayScheduleItems || [])
                .filter(x => x.tour_day_id === day.id)
                .filter(x => {
                  if (!memberRole) return true;
                  const ids = Array.isArray(x.assigned_profile_ids) ? x.assigned_profile_ids : [];
                  return ids.length === 0 || (myPid && ids.includes(myPid));
                })
                .sort((a, b) => a.start_minutes - b.start_minutes);
              return (
                <Section key="schedule" label="Show" UI={UI}>
                  {todayItems.length > 0
                    ? todayItems.map(x => <SchedRow key={x.id} label={x.title} value={fmtMins(x.start_minutes)} UI={UI} />)
                    : <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>No schedule yet — check back closer to show day</div>
                  }
                </Section>
              );

            case "hotel":
              return modEnabled("hotel") && (lodgingLoading || lodgingList.length > 0) ? (
                <Section key="hotel" label="Hotel" UI={UI}>
                  {lodgingLoading ? (
                    <SkeletonRows UI={UI} lines={2} />
                  ) : lodgingList.map((l, i) => (
                    <div key={l.id || i} style={{ marginBottom: i < lodgingList.length - 1 ? 14 : 0 }}>
                      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 3 }}>
                        {l.name}{l.chain ? ` · ${l.chain}` : ""}
                      </div>
                      {l.address && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 3 }}>{l.address}</div>}
                      <div style={{ display: "flex", gap: 16, marginBottom: l.confirmation ? 3 : 0 }}>
                        {l.check_in  && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>In {l.check_in}</div>}
                        {l.check_out && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>Out {l.check_out}</div>}
                      </div>
                      {l.confirmation && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>Conf: {l.confirmation}</div>}
                      {l.phone       && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 2 }}>{fmtPhone(l.phone)}</div>}
                    </div>
                  ))}
                </Section>
              ) : null;

            case "flights":
              return modEnabled("flights") && (flightsLoading || flightsList.length > 0) ? (
                <Section key="flights" label="Flights" UI={UI}>
                  {flightsLoading ? (
                    <SkeletonRows UI={UI} lines={2} />
                  ) : flightsList.map((f, i) => {
                    const depTime = f.scheduled_dep
                      ? new Date(f.scheduled_dep).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                      : f.departure_time || null;
                    const arrTime = f.scheduled_arr
                      ? new Date(f.scheduled_arr).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                      : f.arrival_time || null;
                    return (
                      <div key={f.id || i} style={{ marginBottom: i < flightsList.length - 1 ? 12 : 0 }}>
                        <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 2 }}>
                          {[f.airline_code, f.flight_number].filter(Boolean).join("") || "Flight"}
                        </div>
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>
                          {f.origin || f.origin_city || "—"} → {f.destination || f.destination_city || "—"}
                        </div>
                        {(depTime || arrTime) && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 2 }}>
                            {depTime && `Dep ${depTime}`}{depTime && arrTime && "  ·  "}{arrTime && `Arr ${arrTime}`}
                          </div>
                        )}
                        {(f.terminal || f.gate) && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                            {f.terminal && `Terminal ${f.terminal}`}{f.terminal && f.gate && " · "}{f.gate && `Gate ${f.gate}`}
                          </div>
                        )}
                        {(f.booking_ref || f.confirmation) && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, marginTop: 1 }}>
                            Conf: {f.booking_ref || f.confirmation}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Section>
              ) : null;

            case "weather":
              return modEnabled("weather") && wx ? (
                <div key="weather" style={{ padding: "12px 20px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "center", gap: 12 }}>
                  {wx.status === "loading" ? (
                    <>
                      <span style={{ color: UI.textVFnt, display: "flex" }}><WxIcon size={20} /></span>
                      <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.3px" }}>Checking forecast…</span>
                    </>
                  ) : wx.status === "ready" ? (
                    <>
                      <span style={{ color: UI.textMut, display: "flex" }}><WxIcon condition={wx.condition} size={20} /></span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <span style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>{wx.high}°</span>
                          <span style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>{wx.condition}</span>
                        </div>
                        {wx.low != null && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                            Low {wx.low}°{wx.wind ? ` · ${wx.wind} mph winds` : ""}{wx.precip ? ` · ${wx.precip}% precip` : ""}
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null;

            case "transport":
              return modEnabled("transport") && (transportLoading || transportList.length > 0) ? (
                <Section key="transport" label="Ground Transport" UI={UI}>
                  {transportLoading ? (
                    <SkeletonRows UI={UI} lines={1} />
                  ) : transportList.map((t, i) => {
                    const typeLabel = (t.type || "transfer").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                    return (
                      <div key={t.id || i} style={{ marginBottom: i < transportList.length - 1 ? 12 : 0 }}>
                        <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 2 }}>
                          {typeLabel}{t.company ? ` · ${t.company}` : ""}
                        </div>
                        {t.pickup_time && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>Pickup {t.pickup_time}</div>}
                        {(t.pickup_location || t.dropoff_location) && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                            {t.pickup_location && `From: ${t.pickup_location}`}
                            {t.pickup_location && t.dropoff_location && "  →  "}
                            {t.dropoff_location && `${t.dropoff_location}`}
                          </div>
                        )}
                        {t.driver_name && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                            {t.driver_name}{t.driver_phone ? ` · ${t.driver_phone}` : ""}
                          </div>
                        )}
                        {t.confirmation_number && (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, marginTop: 1 }}>Conf: {t.confirmation_number}</div>
                        )}
                      </div>
                    );
                  })}
                </Section>
              ) : null;

            case "notes":
              return day.notes && modEnabled("notes") ? (
                <Section key="notes" label="Notes" UI={UI}>
                  <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, lineHeight: 1.65 }}>{day.notes}</div>
                </Section>
              ) : null;

            case "local_recs": {
              if (!modEnabled("local_recs")) return null;
              const recCity  = (day.type === "show" && venue?.city) ? venue.city : (day.city || "");
              if (!recCity) return null;
              const recState = venue?.addressState || day.state || "";
              const cityKey  = `${recCity.toLowerCase().trim()}|${recState.toLowerCase().trim()}`;
              const recsData = cityRecs?.[cityKey];
              if (!recsData || recsData.status === "loading") {
                return (
                  <Section key="local_recs" label={`Local Recs · ${recCity}`} UI={UI}>
                    <SkeletonRows UI={UI} lines={2} />
                  </Section>
                );
              }
              const modConf       = (memberModules || []).find(m => m.type === "local_recs");
              const enabledCats   = new Set(
                ["coffee","food","brunch","bars","barbershop","gym","music","massage","dispensary","other"]
                  .filter(c => (modConf?.config || {})[c] !== false)
              );
              const filteredRecs  = (recsData.data || []).filter(r => enabledCats.has(r.category || r.cat || "other"));
              if (!filteredRecs.length) return null;
              // Group by category
              const grouped = {};
              for (const r of filteredRecs) {
                const cat = r.category || r.cat || "other";
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(r);
              }
              const CAT_COLORS = {
                food: "var(--accent)", brunch: "#e8906d", bars: "#9b6bcf", coffee: "#b07840",
                barbershop: "#8b7faf", gym: "#6bbf6b", music: "#c4844a",
                massage: "#b06bcf", dispensary: "var(--success)", other: "#888",
              };
              const CAT_LABELS = {
                food: "Food", brunch: "Brunch", bars: "Bars", coffee: "Coffee",
                barbershop: "Barber / Salon", gym: "Gym", music: "Record Stores",
                massage: "Massage", dispensary: "Dispensary", other: "Other",
              };
              return (
                <Section key="local_recs" label={`Local Recs · ${recCity}`} UI={UI}>
                  {Object.entries(grouped).map(([cat, recs]) => (
                    <div key={cat} style={{ marginBottom: 10 }}>
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: CAT_COLORS[cat] || "#888", marginBottom: 5 }}>
                        {CAT_LABELS[cat] || cat}
                      </div>
                      {recs.map((r, i) => (
                        <div key={r.id || i} style={{ marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.text }}>{r.name}</div>
                              {r.notes && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1, lineHeight: 1.4 }}>{r.notes}</div>}
                            </div>
                            {r.maps_url && (
                              <a href={r.maps_url} target="_blank" rel="noreferrer"
                                style={{ flexShrink: 0, fontFamily: FF, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "3px 7px", textDecoration: "none", letterSpacing: "0.3px" }}>
                                Map
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </Section>
              );
            }

            default:
              return null;
          }
        };

        return orderedTypes.map(renderModule);
      })()}

      {/* Who's in Town — only for band/crew members */}
      {memberRole && (() => {
        if (!memberNetworkProfiles?.length) return null;
        const dayCity = (city || "").toLowerCase().trim();
        if (!dayCity) return null;

        const inTown = inTownToday;

        // Split into connections first, then everyone else
        const inTownConnected = inTown.filter(p => memberConnections?.has(p.id));
        const inTownOthers    = inTown.filter(p => !memberConnections?.has(p.id));

        const myName = memberOwnProfile?.preferred_name || memberOwnProfile?.name || "";
        const renderProfile = (p, isLast) => (
          <NetworkPersonRow
            key={p.id} p={p} isLast={isLast} city={city} myName={myName}
            connected={memberConnections?.has(p.id)}
            onOpen={setInTownOverlay} onToggleConnect={toggleMemberConnection}
            UI={UI} showTeam
          />
        );

        const sectionLabel = inTown.length > 0
          ? `Who's in Town · ${city}${inTown.length > 1 ? ` (${inTown.length})` : ""}`
          : `Who's in Town`;

        return (
          <Section label={sectionLabel} UI={UI}>
            {inTown.length === 0 ? (
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, padding: "2px 0 4px" }}>
                No one in your network is routing through {city} today.
              </div>
            ) : (
              <>
                {/* Connections first */}
                {inTownConnected.length > 0 && (
                  <>
                    {inTownConnected.length > 0 && inTownOthers.length > 0 && (
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.amber, marginBottom: 10 }}>Connections</div>
                    )}
                    {inTownConnected.map((p, i) => renderProfile(p, i === inTownConnected.length - 1 && inTownOthers.length === 0))}
                  </>
                )}

                {/* Divider + others */}
                {inTownConnected.length > 0 && inTownOthers.length > 0 && (
                  <div style={{ borderTop: `1px solid ${UI.panelBdr}`, margin: "12px 0 10px" }}>
                    <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt, marginTop: 10 }}>Others in town</div>
                  </div>
                )}
                {inTownOthers.map((p, i) => renderProfile(p, i === inTownOthers.length - 1))}
              </>
            )}
          </Section>
        );
      })()}


      {/* Off day empty state — truly nothing configured */}
      {isOff && !lodgingLoading && !flightsLoading && !transportLoading && !lodgingList.length && !flightsList.length && !transportList.length && !day.bus_call && !day.notes && (
        <MobileEmpty UI={UI} title={day.type === "travel" ? "Travel day" : "Day off"} hint={city || undefined} />
      )}

      {/* SR-1: Post-show report prompt in DayDetailOverlay */}
      {memberRole && showObj && setShowReportModal && (() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const showPast = showObj.date < todayStr;
        const roleCat  = memberOwnProfile?.role_category || memberRole;
        const canReport = roleCat === 'band' || roleCat === 'crew';
        if (!showPast || !canReport) return null;
        const existingRpt = (showReports || []).find(r => r.show_id === showObj.id && r.profile_id === memberOwnProfile?.id);
        const alreadyDone = !!existingRpt;
        return (
          <div style={{ padding: "0 20px 16px" }}>
            <div style={{ padding: "14px 16px", background: lightMode ? "#f5f3ff" : "#14121a", borderRadius: 12, border: `1px solid ${lightMode ? "#a78bfa44" : "#7c3aed33"}` }}>
              {alreadyDone ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <div style={{ flex: 1, fontFamily: FF, fontSize: 14, color: "var(--success)" }}>Post-show report submitted</div>
                  <button onClick={() => setShowReportModal({ show: showObj, tourId: tour?.id, profileId: memberOwnProfile?.id, roleCategory: roleCat, existingReport: existingRpt })}
                    style={{ fontFamily: FF, fontSize: 12, color: "#7c3aed", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Edit</button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: "#7c3aed" }}>How was the show?</div>
                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut, marginTop: 3 }}>Submit your post-show report</div>
                  </div>
                  <button onClick={() => setShowReportModal({ show: showObj, tourId: tour?.id, profileId: memberOwnProfile?.id, roleCategory: roleCat, existingReport: null })}
                    style={{ fontFamily: FF, fontSize: 14, padding: "9px 18px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer", fontWeight: 600, flexShrink: 0, WebkitTapHighlightColor: "transparent" }}>
                    Report
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* MM-3 — TM controls note */}
      {memberRole && (
        <div style={{ padding: "24px 20px 16px", textAlign: "center" }}>
          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, lineHeight: 1.6 }}>
            Your tour manager controls what's shown here.
          </div>
        </div>
      )}

      {/* Who's in Town — profile detail overlay */}
      {inTownOverlay && (
        <ProfileDetailOverlay
          person={inTownOverlay}
          onClose={() => setInTownOverlay(null)}
          lightMode={lightMode}
          UI={UI}
          memberConnections={memberConnections}
          toggleMemberConnection={toggleMemberConnection}
          memberOwnProfile={memberOwnProfile}
        />
      )}
    </div>
  );
}

// ── Setlist bulk import — AI parse of a pasted song list (mobile) ────────────
async function parseSetlistWithAI(text) {
  const prompt = `You are extracting a band's setlist from pasted text (often from Apple Notes or Google Docs). Each line is usually one song, but formats vary. For each song extract:
- "title": the song title (string, required)
- "song_key": musical key if present, e.g. "C", "Am", "F#" (string or null)
- "tempo": tempo in BPM as a number if present (number or null)
- "tuning": guitar/instrument tuning if present, e.g. "Standard", "Drop D", "Eb" (string or null)
- "notes": any other info such as segues, intro cues, or "acoustic" (string or null)

Ignore set headers like "SET 1", "Encore", track numbering (e.g. "1.", "01 -"), and blank lines. Keep the songs in the order given.

Return ONLY a valid JSON array with no markdown fences or explanation. If no songs are found, return [].

Text to parse:
${text}`;
  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: 3000, messages: [{ role: "user", content: prompt }] }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Day Detail Overlay ────────────────────────────────────────────────────────
function DayDetailOverlay({ tourDays, initialIdx, tour, venues, lightMode, UI, memberRole,
  dayLodging, dayFlights, dayTransport, loadDayLodging, loadDayFlights, loadDayTransport, onClose,
  user, teamId, memberOwnProfile, memberPermissions,
  memberNetworkProfiles, memberConnections, toggleMemberConnection, dayScheduleItems,
  showSetlists, saveShowSetlist, deleteShowSetlist,
  showReports, setShowReportModal }) {

  const [currentIdx, setCurrentIdx]     = useState(initialIdx);
  const [inTownOverlay, setInTownOverlay] = useState(null);
  const [slImport, setSlImport] = useState(null); // setlist bulk-import panel: null | { text, loading, error }
  const day = tourDays[currentIdx] || tourDays[initialIdx];

  const today = new Date().toISOString().slice(0, 10);
  const showObj  = day.show_id ? (tour?.shows || []).find(s => s.id === day.show_id) : null;
  const venue    = showObj ? venues[showObj.venueId] : null;
  const city     = day.city || venue?.city || "";
  const isShow   = day.type === "show";
  const isToday  = day.date === today;
  const sch      = showObj?.advanceTmData?.schedule || {};

  const dateLabel = new Date(day.date + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });

  // Who's-in-town for this day — memoized (the .filter + nested .some over every
  // network profile's routing was recomputed on every render / swipe-drag tick).
  const inTownDay = useMemo(() => {
    const dayCity = (city || "").toLowerCase().trim();
    if (!dayCity || !memberNetworkProfiles?.length) return [];
    return memberNetworkProfiles.filter(p =>
      Array.isArray(p.tour_routing) && p.tour_routing.some(r => {
        const rCity = (r.city || "").toLowerCase().trim();
        return r.date === day.date && (rCity === dayCity || rCity.startsWith(dayCity) || dayCity.startsWith(rCity));
      })
    );
  }, [memberNetworkProfiles, city, day.date]);

  // ── Swipe-to-close (left → right) ─────────────────────────────────────────
  const swipeStartX  = useRef(null);
  const swipeStartY  = useRef(null);
  const swipeLocked  = useRef(null); // 'h' | 'v' | null — set once direction is clear
  const [swipeDx, setSwipeDx] = useState(0); // drives translateX during drag

  const onSwipeTouchStart = (e) => {
    swipeStartX.current = e.touches[0].clientX;
    swipeStartY.current = e.touches[0].clientY;
    swipeLocked.current = null;
  };
  const onSwipeTouchMove = (e) => {
    if (swipeStartX.current === null) return;
    const dx = e.touches[0].clientX - swipeStartX.current;
    const dy = e.touches[0].clientY - swipeStartY.current;
    // Lock direction once we know which axis the user intends
    if (!swipeLocked.current) {
      if (Math.abs(dx) > Math.abs(dy) + 4) swipeLocked.current = 'h';
      else if (Math.abs(dy) > Math.abs(dx) + 4) swipeLocked.current = 'v';
      else return; // not decisive yet
    }
    if (swipeLocked.current !== 'h') return; // vertical — let scroll handle it
    if (dx > 0) { // only track rightward drag
      e.preventDefault();
      setSwipeDx(dx);
    }
  };
  const onSwipeTouchEnd = () => {
    if (swipeLocked.current === 'h' && swipeDx > 72) {
      onClose();
    }
    setSwipeDx(0);
    swipeStartX.current = null;
    swipeLocked.current = null;
  };

  // Lazy-load logistics for this day on mount
  useEffect(() => {
    if (!day?.id) return;
    loadDayLodging?.(day.id);
    loadDayFlights?.(day.id);
    loadDayTransport?.(day.id);
  }, [day?.id]);

  // fetchDone is derived from whether all three cache keys exist in parent state.
  // Using `in` (not truthiness) so an empty-array result also counts as "done".
  const fetchDone = dayLodging  && day.id in dayLodging
                 && dayFlights  && day.id in dayFlights
                 && dayTransport && day.id in dayTransport;

  const lodgingList   = dayLodging?.[day.id]   || [];
  const flightsList   = dayFlights?.[day.id]   || [];
  const transportList = dayTransport?.[day.id] || [];

  const hasLogistics = fetchDone && (lodgingList.length > 0 || flightsList.length > 0 || transportList.length > 0);
  const hasContent   = day.bus_call || isShow || day.notes || hasLogistics;

  // Advance details — visible to everyone (TMs + members with permission)
  const tmAdvData = showObj?.advanceTmData || {};  // camelCase — matches App.jsx show shape
  // PP-1 Phase 3: gate by permissions — default open if no membership row yet
  const canSeeContacts  = !memberPermissions || memberPermissions.can_see_contacts  !== false;
  const canSeeAdvance   = !memberPermissions || memberPermissions.can_see_advance   !== false;

  // Structured advance sections (TM-prefilled data)
  const tmSched = tmAdvData.schedule    || {};
  const tmHosp  = tmAdvData.hospitality || {};
  const tmPark  = tmAdvData.parking     || {};
  const tmGR    = tmAdvData.greenRoom   || {};

  // Show to TMs always; show to members when there is data and they have advance permission
  const hasAdvSection = isShow && showObj && (
    tmSched.venue_access ||
    venue?.loadIn || venue?.parking ||
    tmPark.loading_zone || tmPark.bus_parking || tmPark.passes || tmPark.park_notes ||
    (canSeeAdvance && (isYNSet(tmHosp.hot_meal) || isYNSet(tmHosp.alcohol) || isYNSet(tmHosp.buyout_yn) || tmHosp.buyout || isYNSet(tmHosp.runner) || tmHosp.hosp_contact || tmHosp.hospo_notes || tmHosp.menu_url)) ||
    tmGR.gr_access || tmGR.rooms || isYNSet(tmGR.shower) || isYNSet(tmGR.private_bath) ||
    tmAdvData.notes
  );

  // ── Guest list requests (band/crew, show days) ────────────────────────────
  const [glRequests,   setGlRequests]   = useState([]);
  const [glFormOpen,   setGlFormOpen]   = useState(false);
  const [glSubmitting, setGlSubmitting] = useState(false);
  const [glForm, setGlForm] = useState({ firstName: "", lastName: "", phone: "", email: "", tickets: 1, access: [], note: "" });
  const GL_ACCESS_TAGS = ["GA","AA","Family","Media","VIP","Backstage"];
  const GL_ACCESS_COLOR = { GA: "#888", AA: "var(--accent)", Family: "var(--success)", Media: "#6a8faf", VIP: "#8a6faf", Backstage: "var(--accent)" };
  const toggleGlFormAccess = (tag) => setGlForm(p => {
    const cur = Array.isArray(p.access) ? p.access : [];
    return { ...p, access: cur.includes(tag) ? cur.filter(a => a !== tag) : [...cur, tag] };
  });
  const glShowId = showObj?.id;

  useEffect(() => {
    if (!memberRole || !isShow || !glShowId) return;
    supabase.from("guest_list_requests")
      .select("*")
      .eq("show_id", glShowId)
      .order("created_at", { ascending: true })
      .then(({ data }) => setGlRequests(data || []));
  }, [glShowId, memberRole, isShow]);

  const submitGlRequest = async () => {
    if (!glForm.firstName.trim() && !glForm.lastName.trim()) return;
    setGlSubmitting(true);
    const row = {
      id: `glr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      show_id:                 glShowId,
      tour_id:                 tour?.id || "",
      team_id:                 teamId,
      submitted_by_profile_id: memberOwnProfile?.id || null,
      submitted_by_name:       memberOwnProfile?.name || user?.email || "Unknown",
      first_name:  glForm.firstName.trim(),
      last_name:   glForm.lastName.trim(),
      phone:       glForm.phone.trim(),
      email:       glForm.email.trim(),
      tickets:     Math.max(1, glForm.tickets),
      access:      glForm.access,
      note:        glForm.note.trim(),
      status:      "pending",
    };
    const { data, error } = await supabase.from("guest_list_requests").insert(row).select().single();
    if (!error && data) {
      setGlRequests(p => [...p, data]);
      setGlForm({ firstName: "", lastName: "", phone: "", email: "", tickets: 1, access: [], note: "" });
      setGlFormOpen(false);
    }
    setGlSubmitting(false);
  };

  const deleteGlRequest = async (id) => {
    await supabase.from("guest_list_requests").delete().eq("id", id);
    setGlRequests(p => p.filter(r => r.id !== id));
  };

  const GL_STATUS_COLOR = { pending: UI.amber, confirmed: UI.green, denied: "var(--danger)" };
  const GL_STATUS_LABEL = { pending: "Pending", confirmed: "Confirmed ✓", denied: "Not approved" };

  return (
    <div
      onTouchStart={onSwipeTouchStart}
      onTouchMove={onSwipeTouchMove}
      onTouchEnd={onSwipeTouchEnd}
      style={{
        position: "fixed",
        top: "calc(48px + env(safe-area-inset-top, 0px))",
        left: 0, right: 0,
        bottom: "calc(56px + env(safe-area-inset-bottom, 0px))",
        zIndex: 400,
        background: UI.bg, display: "flex", flexDirection: "column",
        transform: swipeDx > 0 ? `translateX(${swipeDx}px)` : undefined,
        transition: swipeDx === 0 ? "transform 0.2s ease" : undefined,
        animation: swipeDx === 0 ? "slideUpFade 0.22s ease" : undefined,
        opacity: swipeDx > 0 ? Math.max(0.5, 1 - swipeDx / 300) : 1,
      }}>
      <style>{`@keyframes slideUpFade { from { transform: translateY(18px); opacity: 0; } to { transform: translateY(0); opacity: 1; } } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 20px 12px",
        borderBottom: `1px solid ${UI.panelBdr}`,
        flexShrink: 0,
        background: isToday ? (lightMode ? "#fffdf5" : "#0f0e08") : UI.bg,
      }}>
        {/* Back button */}
        <button onClick={onClose}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: UI.textMut, lineHeight: 1, padding: "0 4px 0 0",
            WebkitTapHighlightColor: "transparent", flexShrink: 0,
          }}><ChevronIcon dir="left" size={18} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase",
            color: isToday ? UI.amber : UI.textFnt, marginBottom: 2,
          }}>
            {isToday ? "Today · " : ""}{dateLabel}
            {isShow && (
              <span style={{
                marginLeft: 6, fontSize: 11, letterSpacing: "1.5px",
                color: UI.amber, background: UI.amdBg, padding: "1px 6px", borderRadius: 4,
              }}>SHOW</span>
            )}
          </div>
          <div style={{
            fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {venue?.name || city || (isShow ? "Show Day" : "Day Off")}
          </div>
          {venue?.name && city && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>{city}</div>
          )}
        </div>
        {/* Day Sheet link — only shown when TM has generated a token */}
        {day.day_sheet_token && (
          <button
            onClick={() => window.open(`/api/day-sheet?token=${day.day_sheet_token}&internal=1`, "_blank")}
            title="Open Day Sheet"
            style={{
              background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 8,
              cursor: "pointer", color: UI.textMut, padding: "5px 8px",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, WebkitTapHighlightColor: "transparent",
            }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
          </button>
        )}
        {/* Prev / Next navigation */}
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <button onClick={() => setCurrentIdx(i => i - 1)} disabled={currentIdx <= 0} aria-label="Previous day"
            style={{
              background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 8,
              cursor: currentIdx <= 0 ? "default" : "pointer",
              color: currentIdx <= 0 ? UI.panelBdr : UI.textMut,
              lineHeight: 1, padding: "8px 12px",
              WebkitTapHighlightColor: "transparent",
            }}><ChevronIcon dir="left" size={14} /></button>
          <button onClick={() => setCurrentIdx(i => i + 1)} disabled={currentIdx >= tourDays.length - 1} aria-label="Next day"
            style={{
              background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 8,
              cursor: currentIdx >= tourDays.length - 1 ? "default" : "pointer",
              color: currentIdx >= tourDays.length - 1 ? UI.panelBdr : UI.textMut,
              lineHeight: 1, padding: "8px 12px",
              WebkitTapHighlightColor: "transparent",
            }}><ChevronIcon size={14} /></button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 60, WebkitOverflowScrolling: "touch" }}>

        {/* Bus Call */}
        {day.bus_call && (
          <div style={{
            padding: "14px 20px", borderBottom: `1px solid ${UI.panelBdr}`,
            display: "flex", alignItems: "center", gap: 14,
            background: lightMode ? "#fffbee" : "#100f08",
          }}>
            <span style={{ color: UI.amber, display: "flex" }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="13" rx="2"/><path d="M1 11h22M5 17v2M19 17v2"/><circle cx="6" cy="17" r="1" fill="currentColor"/><circle cx="18" cy="17" r="1" fill="currentColor"/></svg></span>
            <div>
              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.amber, marginBottom: 3 }}>Bus Call</div>
              <div style={{ fontFamily: FF, fontSize: 28, fontWeight: 700, color: UI.text, letterSpacing: "-0.5px" }}>{fmtTime12(day.bus_call)}</div>
            </div>
          </div>
        )}

        {/* Show schedule — reads from TM-curated day_schedule_items only */}
        {isShow && showObj && (() => {
          const myPid = memberOwnProfile?.id;
          const overlayItems = (dayScheduleItems || [])
            .filter(x => x.tour_day_id === day.id)
            .filter(x => {
              if (!memberRole) return true;
              const ids = Array.isArray(x.assigned_profile_ids) ? x.assigned_profile_ids : [];
              return ids.length === 0 || (myPid && ids.includes(myPid));
            })
            .sort((a, b) => a.start_minutes - b.start_minutes);
          return (
            <Section label="Show" UI={UI}>
              {venue?.name && <InfoRow label="Venue" value={venue.name} UI={UI} />}
              {(venue?.addressStreet || venue?.city) && (
                <InfoRow label="Address"
                  value={[venue.addressStreet, venue.city, venue.addressState].filter(Boolean).join(", ")}
                  href={`https://maps.apple.com/?q=${encodeURIComponent([venue.addressStreet, venue.city, venue.addressState].filter(Boolean).join(", "))}`}
                  UI={UI} />
              )}
              {venue?.capacity && <InfoRow label="Capacity" value={Number(venue.capacity).toLocaleString()} UI={UI} />}
              {venue?.phone && <InfoRow label="Phone" value={fmtPhone(venue.phone)} href={`tel:${venue.phone}`} UI={UI} />}
              {venue?.website && (
                <InfoRow label="Website" value={venue.website}
                  href={venue.website.startsWith("http") ? venue.website : `https://${venue.website}`}
                  UI={UI} />
              )}
              {overlayItems.length > 0
                ? overlayItems.map(x => <SchedRow key={x.id} label={x.title} value={fmtMins(x.start_minutes)} UI={UI} />)
                : <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>No schedule yet</div>
              }
            </Section>
          );
        })()}

        {/* Advance details — band/crew only, show days; gated by PP-1 Phase 3 permissions */}
        {hasAdvSection && (() => {
          const hasPark = tmSched.venue_access || venue?.loadIn || venue?.parking ||
            tmPark.loading_zone || tmPark.bus_parking || tmPark.passes || tmPark.park_notes;
          const hasHosp = canSeeAdvance && (
            isYNSet(tmHosp.hot_meal) || isYNSet(tmHosp.alcohol) || isYNSet(tmHosp.buyout_yn) ||
            tmHosp.buyout || isYNSet(tmHosp.runner) || tmHosp.hosp_contact ||
            tmHosp.hospo_notes || tmHosp.menu_url
          );
          const tmGRWifi = Array.isArray(tmGR.wifi_networks) ? tmGR.wifi_networks.filter(n => n.network) : [];
          const hasGR = tmGR.gr_access || tmGR.rooms || isYNSet(tmGR.shower) || isYNSet(tmGR.private_bath) || tmGR.gr_layout || tmGRWifi.length > 0;
          return (
            <>
              {hasPark && (
                <AdvSection label="Parking &amp; Load-in" UI={UI}>
                  {(tmSched.venue_access || venue?.loadIn) && (
                    <AdvInfoItem label="Venue Access" value={fmtTime12(tmSched.venue_access) || venue.loadIn} UI={UI} />
                  )}
                  {venue?.parking      && <AdvInfoItem label="Parking"        value={venue.parking}          UI={UI} />}
                  {tmPark.loading_zone && <AdvInfoItem label="Loading Zone"   value={tmPark.loading_zone}    UI={UI} />}
                  {tmPark.bus_parking  && <AdvInfoItem label="Bus / Trailer"  value={tmPark.bus_parking}     UI={UI} />}
                  {tmPark.passes       && <AdvInfoItem label="Parking Passes" value={tmPark.passes}          UI={UI} />}
                  {tmPark.park_notes   && <AdvInfoItem label="Notes"          value={tmPark.park_notes}      UI={UI} />}
                </AdvSection>
              )}

              {hasHosp && (
                <AdvSection label="Hospitality" UI={UI}>
                  {isYNSet(tmHosp.hot_meal)  && <AdvInfoItem label="Hot Meal"        value={fmtYN(tmHosp.hot_meal)}   UI={UI} />}
                  {isYNSet(tmHosp.alcohol)   && <AdvInfoItem label="Alcohol on Rider" value={fmtYN(tmHosp.alcohol)}   UI={UI} />}
                  {isYNSet(tmHosp.buyout_yn) && <AdvInfoItem label="Buyout"          value={fmtYN(tmHosp.buyout_yn)}  UI={UI} />}
                  {tmHosp.buyout              && <AdvInfoItem label="Buyout Amount"   value={tmHosp.buyout}            UI={UI} />}
                  {isYNSet(tmHosp.runner)    && <AdvInfoItem label="Runner"          value={fmtYN(tmHosp.runner)}     UI={UI} />}
                  {tmHosp.hosp_contact        && <AdvInfoItem label="Catering Contact" value={tmHosp.hosp_contact}    UI={UI} />}
                  {tmHosp.hospo_notes         && <AdvInfoItem label="Notes"           value={tmHosp.hospo_notes}      UI={UI} />}
                  {tmHosp.menu_url && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textMut, marginBottom: 6 }}>Menu</div>
                      <a href={tmHosp.menu_url.startsWith("http") ? tmHosp.menu_url : `https://${tmHosp.menu_url}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.amber, textDecoration: "none", background: `${UI.amber}14`, border: `1px solid ${UI.amber}30`, borderRadius: 8, padding: "6px 12px" }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        View Menu
                      </a>
                    </div>
                  )}
                </AdvSection>
              )}

              {hasGR && (
                <AdvSection label="Green Room" UI={UI}>
                  {tmGR.rooms                && <AdvInfoItem label="Rooms"      value={tmGR.rooms}               UI={UI} />}
                  {tmGR.gr_access            && <AdvInfoItem label="Access"     value={tmGR.gr_access}           UI={UI} />}
                  {isYNSet(tmGR.shower)      && <AdvInfoItem label="Shower"     value={fmtYN(tmGR.shower)}       UI={UI} />}
                  {isYNSet(tmGR.private_bath)&& <AdvInfoItem label="Priv. Bath" value={fmtYN(tmGR.private_bath)} UI={UI} />}
                  {tmGR.gr_layout            && <AdvInfoItem label="Layout"     value={tmGR.gr_layout}           UI={UI} />}
                  {tmGRWifi.map((net, i) => (
                    <AdvInfoItem key={i} label={tmGRWifi.length > 1 ? `Wi-Fi ${i + 1}` : "Wi-Fi"} value={net.network + (net.password ? ` · ${net.password}` : "")} UI={UI} />
                  ))}
                </AdvSection>
              )}

              {tmAdvData.notes && (
                <AdvSection label="TM Notes" UI={UI}>
                  <AdvInfoItem label="" value={tmAdvData.notes} UI={UI} />
                </AdvSection>
              )}
            </>
          );
        })()}

        {/* ── Deal (Guarantee + Settlement) — permission gated ── */}
        {isShow && showObj && (() => {
          const isTM2 = !memberRole || !memberPermissions;
          const canSeeGuar = isTM2 || memberPermissions?.can_see_guarantee  !== false;
          const canSeeSett = isTM2 || memberPermissions?.can_see_settlement !== false;
          if (!canSeeGuar && !canSeeSett) return null;
          const sett = showObj.settlement || {};
          const hasGuar = canSeeGuar && (sett.dealType || sett.guarantee || sett.deposit != null);
          const hasSett = canSeeSett && (sett.vsTerms || sett.paymentMethod || sett.depositReceived || sett.settled != null);
          if (!hasGuar && !hasSett) return null;
          const fmtMoney = (v) => { const n = Number((v + "").replace(/[^0-9.]/g, "")); return !isNaN(n) && n > 0 ? `$${n.toLocaleString()}` : v; };
          return (
            <>
              {hasGuar && (
                <Section label="Deal" UI={UI}>
                  {sett.dealType  && <InfoRow label="Deal Type"  value={sett.dealType}            UI={UI} />}
                  {sett.guarantee && <InfoRow label="Guarantee"  value={fmtMoney(sett.guarantee)} UI={UI} />}
                  {sett.deposit   && <InfoRow label="Deposit"    value={fmtMoney(sett.deposit)}   UI={UI} />}
                </Section>
              )}
              {hasSett && (
                <Section label="Settlement" UI={UI}>
                  {sett.vsTerms         && <InfoRow label="Deal Terms"   value={sett.vsTerms}               UI={UI} />}
                  {sett.paymentMethod   && <InfoRow label="Payment"      value={sett.paymentMethod}         UI={UI} />}
                  {sett.depositReceived && <InfoRow label="Deposit Rcvd" value={fmtMoney(sett.depositReceived)} UI={UI} />}
                  {sett.settled != null && <InfoRow label="Settled"      value={sett.settled ? "Yes ✓" : "No"} UI={UI} />}
                </Section>
              )}
            </>
          );
        })()}

        {/* ── Advance status + promoter link (TM only) ── */}
        {isShow && showObj && !memberRole && (() => {
          const statusInfo = (() => {
            const s = showObj.status || "unstarted";
            if (s === "confirmed") return { color: UI.green,  label: "Confirmed" };
            if (s === "advancing") return { color: UI.amber,  label: "Advancing" };
            if (s === "dayOf")     return { color: "#60b8ff", label: "Day-Of"    };
            return                        { color: UI.textFnt, label: "Unstarted" };
          })();
          const advUrl = showObj.advanceToken ? `${window.location.origin}/advance/${showObj.advanceToken}` : null;
          return (
            <Section label="Advance" UI={UI}>
              <InfoRow label="Status" value={statusInfo.label} UI={UI} />
              {advUrl && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginBottom: 8, wordBreak: "break-all", lineHeight: 1.6, padding: "8px 10px", background: UI.panelBg, borderRadius: 8 }}>{advUrl}</div>
                  <button
                    onClick={() => navigator.clipboard.writeText(advUrl).catch(() => {})}
                    style={{ width: "100%", padding: "11px", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, cursor: "pointer", fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, WebkitTapHighlightColor: "transparent" }}>
                    Copy Advance Link
                  </button>
                </div>
              )}
            </Section>
          );
        })()}

        {/* Guest List — band/crew can submit requests per show */}
        {memberRole && isShow && showObj && (
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${UI.panelBdr}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt }}>My Guests</div>
              {!glFormOpen && (
                <button onClick={() => setGlFormOpen(true)}
                  style={{ fontFamily: FF, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amber}22`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                  + Request a guest
                </button>
              )}
            </div>

            {/* Existing requests */}
            {glRequests.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: glFormOpen ? 12 : 0 }}>
                {glRequests.map(r => (
                  <div key={r.id} style={{ padding: "8px 10px", background: UI.panelBg, borderRadius: 8, border: `1px solid ${UI.panelBdr}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text }}>
                          {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                          {r.tickets > 1 && <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, fontWeight: 400, marginLeft: 5 }}>×{r.tickets}</span>}
                        </div>
                        {(r.email || r.phone) && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>{[r.email, r.phone].filter(Boolean).join(" · ")}</div>}
                        {r.note && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>{r.note}</div>}
                      </div>
                      <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: GL_STATUS_COLOR[r.status] || UI.textMut }}>
                        {GL_STATUS_LABEL[r.status] || r.status}
                      </span>
                      {r.status === "pending" && (
                        <button onClick={() => deleteGlRequest(r.id)}
                          style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", padding: 0, lineHeight: 1, WebkitTapHighlightColor: "transparent", display: "inline-flex", alignItems: "center" }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                      )}
                    </div>
                    {Array.isArray(r.access) && r.access.length > 0 && (
                      <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                        {r.access.map(tag => (
                          <span key={tag} style={{ fontFamily: FF, fontSize: 11, padding: "2px 8px", borderRadius: 4, border: `1px solid ${GL_ACCESS_COLOR[tag] || UI.panelBdr}`, color: GL_ACCESS_COLOR[tag] || UI.textMut, letterSpacing: "0.2px" }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {glRequests.length === 0 && !glFormOpen && (
              <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>No guests requested yet.</div>
            )}

            {/* Add form */}
            {glFormOpen && (
              <div style={{ background: UI.panelBg, borderRadius: 8, border: `1px solid ${UI.panelBdr}`, padding: "12px 14px", marginTop: glRequests.length > 0 ? 0 : 0 }}>
                {/* Name row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>First Name</div>
                    <input
                      autoCapitalize="words"
                      style={{ width: "100%", boxSizing: "border-box", background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px", fontFamily: FF, fontSize: 14, color: UI.text, outline: "none" }}
                      placeholder="First name"
                      value={glForm.firstName}
                      onChange={e => setGlForm(p => ({ ...p, firstName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>Last Name</div>
                    <input
                      autoCapitalize="words"
                      style={{ width: "100%", boxSizing: "border-box", background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px", fontFamily: FF, fontSize: 14, color: UI.text, outline: "none" }}
                      placeholder="Last name"
                      value={glForm.lastName}
                      onChange={e => setGlForm(p => ({ ...p, lastName: e.target.value }))}
                    />
                  </div>
                </div>
                {/* Contact row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>Phone (optional)</div>
                    <input
                      type="tel" inputMode="tel" autoComplete="tel"
                      style={{ width: "100%", boxSizing: "border-box", background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px", fontFamily: FF, fontSize: 14, color: UI.text, outline: "none" }}
                      placeholder="Phone number"
                      value={glForm.phone}
                      onChange={e => setGlForm(p => ({ ...p, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>Email (optional)</div>
                    <input
                      type="email" inputMode="email" autoCapitalize="none" autoComplete="email"
                      style={{ width: "100%", boxSizing: "border-box", background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px", fontFamily: FF, fontSize: 14, color: UI.text, outline: "none" }}
                      placeholder="Email address"
                      value={glForm.email}
                      onChange={e => setGlForm(p => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                </div>
                {/* Access chips */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 6 }}>Access (optional)</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {GL_ACCESS_TAGS.map(tag => {
                      const on = glForm.access.includes(tag);
                      return (
                        <button key={tag} onClick={() => toggleGlFormAccess(tag)}
                          style={{ fontFamily: FF, fontSize: 12, padding: "5px 12px", borderRadius: 4, border: "1px solid",
                            background: on ? GL_ACCESS_COLOR[tag] : "none",
                            color: on ? "#0d0d0d" : UI.textMut,
                            borderColor: on ? GL_ACCESS_COLOR[tag] : UI.panelBdr,
                            fontWeight: on ? 700 : 400,
                            cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Tickets + Note row */}
                <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 8, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>Tickets</div>
                    <input
                      type="number" min="1" max="10"
                      style={{ width: "100%", boxSizing: "border-box", background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px", fontFamily: FF, fontSize: 14, color: UI.text, outline: "none", textAlign: "center" }}
                      value={glForm.tickets}
                      onChange={e => setGlForm(p => ({ ...p, tickets: Math.max(1, parseInt(e.target.value) || 1) }))}
                    />
                  </div>
                  <div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 4 }}>Note (optional)</div>
                    <input
                      style={{ width: "100%", boxSizing: "border-box", background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px", fontFamily: FF, fontSize: 14, color: UI.text, outline: "none" }}
                      placeholder="Any notes for the TM"
                      value={glForm.note}
                      onChange={e => setGlForm(p => ({ ...p, note: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={submitGlRequest}
                    disabled={glSubmitting || (!glForm.firstName.trim() && !glForm.lastName.trim())}
                    style={{ flex: 1, fontFamily: FF, fontSize: 14, fontWeight: 600, color: "var(--accent-fg)", background: UI.amber, border: "none", borderRadius: 8, padding: "10px 0", cursor: "pointer", opacity: glSubmitting ? 0.6 : 1 }}>
                    {glSubmitting ? "Submitting…" : "Submit Request"}
                  </button>
                  <button
                    onClick={() => { setGlFormOpen(false); setGlForm({ firstName: "", lastName: "", phone: "", email: "", tickets: 1, access: [], note: "" }); }}
                    style={{ fontFamily: FF, fontSize: 14, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "10px 16px", cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Logistics — single loading state covers hotel/flights/transport together.
            Sections only render once all three fetches have settled (fetchDone),
            so no section ever flashes "Loading…" and then vanishes when empty. */}
        {!fetchDone ? (
          <div style={{ padding: "18px 20px", borderBottom: `1px solid ${UI.panelBdr}` }}>
            <SkeletonRows UI={UI} lines={2} />
          </div>
        ) : (
          <>
            {/* Hotel */}
            {lodgingList.length > 0 && (
              <Section label="Hotel" UI={UI}>
                {lodgingList.map((l, i) => (
                  <div key={l.id || i} style={{ marginBottom: i < lodgingList.length - 1 ? 14 : 0 }}>
                    <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 3 }}>
                      {l.name}{l.chain ? ` · ${l.chain}` : ""}
                    </div>
                    {l.address && (
                      <a href={`https://maps.apple.com/?q=${encodeURIComponent(l.address)}`}
                        style={{ display: "block", fontFamily: FF, fontSize: 11, color: UI.amber, textDecoration: "none", marginBottom: 3 }}>
                        {l.address}
                      </a>
                    )}
                    <div style={{ display: "flex", gap: 16, marginBottom: l.confirmation ? 3 : 0 }}>
                      {l.check_in  && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>In {l.check_in}</div>}
                      {l.check_out && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>Out {l.check_out}</div>}
                    </div>
                    {l.confirmation && <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber }}>Conf: {l.confirmation}</div>}
                    {l.phone && (
                      <a href={`tel:${l.phone.replace(/[^+\d]/g, "")}`}
                        style={{ display: "block", fontFamily: FF, fontSize: 11, color: UI.amber, textDecoration: "none", marginTop: 2 }}>
                        {fmtPhone(l.phone)}
                      </a>
                    )}
                  </div>
                ))}
              </Section>
            )}

            {/* Flights */}
            {flightsList.length > 0 && (
              <Section label="Flights" UI={UI}>
                {flightsList.map((f, i) => {
                  const depTime = f.scheduled_dep
                    ? new Date(f.scheduled_dep).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                    : f.departure_time || null;
                  const arrTime = f.scheduled_arr
                    ? new Date(f.scheduled_arr).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                    : f.arrival_time || null;
                  return (
                    <div key={f.id || i} style={{ marginBottom: i < flightsList.length - 1 ? 12 : 0 }}>
                      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 2 }}>
                        {[f.airline_code, f.flight_number].filter(Boolean).join("") || "Flight"}
                      </div>
                      <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>
                        {f.origin || f.origin_city || "—"} → {f.destination || f.destination_city || "—"}
                      </div>
                      {(depTime || arrTime) && (
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 2 }}>
                          {depTime && `Dep ${depTime}`}{depTime && arrTime && "  ·  "}{arrTime && `Arr ${arrTime}`}
                        </div>
                      )}
                      {(f.terminal || f.gate) && (
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                          {f.terminal && `Terminal ${f.terminal}`}{f.terminal && f.gate && " · "}{f.gate && `Gate ${f.gate}`}
                        </div>
                      )}
                      {(f.booking_ref || f.confirmation) && (
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, marginTop: 1 }}>
                          Conf: {f.booking_ref || f.confirmation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Section>
            )}

            {/* Ground Transport */}
            {transportList.length > 0 && (
              <Section label="Ground Transport" UI={UI}>
                {transportList.map((t, i) => {
                  const typeLabel = (t.type || "transfer").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <div key={t.id || i} style={{ marginBottom: i < transportList.length - 1 ? 12 : 0 }}>
                      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 2 }}>
                        {typeLabel}{t.company ? ` · ${t.company}` : ""}
                      </div>
                      {t.pickup_time && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>Pickup {t.pickup_time}</div>}
                      {(t.pickup_location || t.dropoff_location) && (
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                          {t.pickup_location && `From: ${t.pickup_location}`}
                          {t.pickup_location && t.dropoff_location && "  →  "}
                          {t.dropoff_location && t.dropoff_location}
                        </div>
                      )}
                      {t.driver_name && (
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                          {t.driver_name}{t.driver_phone ? ` · ${t.driver_phone}` : ""}
                        </div>
                      )}
                      {t.confirmation_number && (
                        <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, marginTop: 1 }}>Conf: {t.confirmation_number}</div>
                      )}
                    </div>
                  );
                })}
              </Section>
            )}
          </>
        )}

        {/* Notes */}
        {day.notes && (
          <Section label="Notes" UI={UI}>
            <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, lineHeight: 1.65 }}>{day.notes}</div>
          </Section>
        )}

        {/* SL-1: Setlist — band members only (read-only by default; editable when can_edit_setlist) */}
        {showObj && memberPermissions?.tour_role === 'band' && (() => {
          const setsForShow = (showSetlists || [])
            .filter(s => s.show_id === showObj.id)
            .sort((a, b) => (a.set_number || 0) - (b.set_number || 0));
          const canEdit = !!memberPermissions?.can_edit_setlist;
          if (!setsForShow.length && !canEdit) return null; // hide entirely if nothing to read

          const writeSongs = (setNumber, newSongs) => {
            if (!saveShowSetlist) return;
            saveShowSetlist({ showId: showObj.id, tourId: tour.id, setNumber, songs: newSongs });
          };
          const addSet = () => {
            if (!saveShowSetlist) return;
            const setNumbers = setsForShow.map(s => s.set_number);
            const nextNum = setNumbers.length ? Math.max(...setNumbers) + 1 : 1;
            saveShowSetlist({ showId: showObj.id, tourId: tour.id, setNumber: nextNum, songs: [] });
          };
          const importTarget = setsForShow.length ? `Adds to Set ${setsForShow[setsForShow.length - 1].set_number}` : "Creates Set 1";
          const doImport = async () => {
            const txt = (slImport?.text || "").trim();
            if (!txt) return;
            setSlImport(p => ({ ...p, loading: true, error: "" }));
            try {
              const songs = await parseSetlistWithAI(txt);
              if (!songs.length) { setSlImport(p => ({ ...p, loading: false, error: "No songs found. Try a different paste." })); return; }
              const withIds = songs.map(s => ({ id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`, title: s.title || "", song_key: s.song_key || "", tempo: s.tempo ?? null, tuning: s.tuning || "", notes: s.notes || "" }));
              const lastSet = setsForShow[setsForShow.length - 1];
              if (lastSet) writeSongs(lastSet.set_number, [...(lastSet.songs || []), ...withIds]);
              else saveShowSetlist({ showId: showObj.id, tourId: tour.id, setNumber: 1, songs: withIds });
              setSlImport(null);
            } catch { setSlImport(p => ({ ...p, loading: false, error: "Parsing failed. Please try again." })); }
          };

          const inputStyle = {
            width: "100%", padding: "8px 9px", border: `1px solid ${UI.panelBdr}`,
            borderRadius: 8, fontSize: 14, fontFamily: FF, color: UI.text,
            background: UI.panel, outline: "none", boxSizing: "border-box", minWidth: 0,
          };
          const smallBtn = {
            padding: "5px 9px", border: `1px solid ${UI.panelBdr}`, background: UI.panel,
            color: UI.textMid, borderRadius: 4, fontSize: 11, fontFamily: FF, cursor: "pointer",
          };
          const ghostBtn = {
            padding: "8px 12px", border: `1px solid ${UI.panelBdr}`, background: "transparent",
            color: UI.text, borderRadius: 8, fontSize: 12, fontFamily: FF, cursor: "pointer",
          };

          return (
            <Section label="Setlist" UI={UI}>
              {setsForShow.length === 0 && canEdit && (
                <div style={{ padding: "8px 0", fontFamily: FF, fontSize: 12, color: UI.textMid }}>
                  No setlist yet.
                  <button onClick={addSet} style={{ ...ghostBtn, marginLeft: 10 }}>+ Create Set 1</button>
                  <button onClick={() => setSlImport({ text: "", loading: false, error: "" })} style={{ ...ghostBtn, marginLeft: 8 }}>Import</button>
                </div>
              )}

              {setsForShow.map((set, sIdx) => {
                const songs = set.songs || [];
                const updateSong = (idx, patch) => writeSongs(set.set_number, songs.map((s, i) => i === idx ? { ...s, ...patch } : s));
                const removeSong = (idx) => writeSongs(set.set_number, songs.filter((_, i) => i !== idx));
                const moveSong = (from, to) => {
                  if (to < 0 || to >= songs.length) return;
                  const arr = [...songs];
                  const [moved] = arr.splice(from, 1);
                  arr.splice(to, 0, moved);
                  writeSongs(set.set_number, arr);
                };
                const addSong = () => writeSongs(set.set_number, [...songs, { id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`, title: "", song_key: "", tempo: null, tuning: "", notes: "" }]);
                const removeSet = () => {
                  if (!deleteShowSetlist || !set.id) return;
                  if (!window.confirm(`Delete Set ${set.set_number}?`)) return;
                  deleteShowSetlist(set.id);
                };

                return (
                  <div key={set.id || sIdx} style={{ marginTop: sIdx === 0 ? 4 : 18 }}>
                    {/* Set header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.8, color: UI.textFnt, fontWeight: 700 }}>
                        SET {set.set_number}{songs.length ? ` · ${songs.length}` : ""}
                      </div>
                      {canEdit && set.id && (
                        <button onClick={removeSet} style={{ ...smallBtn, color: "#c46060", borderColor: `${UI.panelBdr}` }}>Delete set</button>
                      )}
                    </div>

                    {/* Songs */}
                    {songs.length === 0 && (
                      <div style={{ padding: "8px 0", fontFamily: FF, fontSize: 12, color: UI.textVFnt, fontStyle: "italic" }}>
                        No songs yet.
                      </div>
                    )}

                    {songs.map((song, idx) => {
                      const meta = [
                        song.song_key && song.song_key.trim(),
                        (song.tempo !== null && song.tempo !== undefined && song.tempo !== "") ? `${song.tempo} BPM` : null,
                        song.tuning && song.tuning.trim(),
                      ].filter(Boolean).join("  ·  ");

                      if (!canEdit) {
                        // Read-only view
                        return (
                          <div key={song.id || idx} style={{
                            display: "flex", gap: 10, padding: "8px 0",
                            borderTop: idx === 0 ? "none" : `1px solid ${UI.panelBdr}`,
                          }}>
                            <span style={{ fontFamily: FF, fontSize: 14, color: UI.textFnt, fontWeight: 700, minWidth: 22 }}>{idx + 1}.</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: FF, fontSize: 14, color: UI.text, fontWeight: 600, lineHeight: 1.3 }}>
                                {song.title || <span style={{ color: UI.textVFnt, fontStyle: "italic" }}>Untitled</span>}
                              </div>
                              {meta && (
                                <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMid, marginTop: 2, letterSpacing: 0.3 }}>{meta}</div>
                              )}
                              {song.notes && (
                                <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, marginTop: 4, lineHeight: 1.5, fontStyle: "italic" }}>{song.notes}</div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Edit view
                      return (
                        <div key={song.id || idx} style={{
                          padding: "10px 0", borderTop: idx === 0 ? "none" : `1px solid ${UI.panelBdr}`,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ fontFamily: FF, fontSize: 14, color: UI.textFnt, fontWeight: 700, minWidth: 22 }}>{idx + 1}.</span>
                            <input value={song.title || ""} onChange={e => updateSong(idx, { title: e.target.value })} placeholder="Song title" style={{ ...inputStyle, fontWeight: 600 }}/>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: 6, marginBottom: 6, paddingLeft: 30 }}>
                            <input value={song.song_key || ""} onChange={e => updateSong(idx, { song_key: e.target.value })} placeholder="Key" style={inputStyle}/>
                            <input value={song.tempo ?? ""} type="number" inputMode="numeric" min="0" onChange={e => updateSong(idx, { tempo: e.target.value === "" ? null : Number(e.target.value) })} placeholder="BPM" style={inputStyle}/>
                            <input value={song.tuning || ""} onChange={e => updateSong(idx, { tuning: e.target.value })} placeholder="Tuning" style={inputStyle}/>
                          </div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center", paddingLeft: 30 }}>
                            <input value={song.notes || ""} onChange={e => updateSong(idx, { notes: e.target.value })} placeholder="Notes (optional)" style={{ ...inputStyle, flex: 1 }}/>
                            <button onClick={() => moveSong(idx, idx - 1)} disabled={idx === 0} style={{ ...smallBtn, opacity: idx === 0 ? 0.35 : 1 }} aria-label="Move up">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                            </button>
                            <button onClick={() => moveSong(idx, idx + 1)} disabled={idx === songs.length - 1} style={{ ...smallBtn, opacity: idx === songs.length - 1 ? 0.35 : 1 }} aria-label="Move down">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            <button onClick={() => removeSong(idx)} style={{ ...smallBtn, color: "#c46060" }} aria-label="Remove song">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {canEdit && (
                      <button onClick={addSong} style={{ ...ghostBtn, marginTop: 10 }}>+ Add song</button>
                    )}
                  </div>
                );
              })}

              {canEdit && setsForShow.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                  <button onClick={addSet} style={{ ...ghostBtn, flex: 1 }}>+ Add another set</button>
                  <button onClick={() => setSlImport({ text: "", loading: false, error: "" })} style={{ ...ghostBtn, flex: 1 }}>Import setlist</button>
                </div>
              )}

              {canEdit && slImport && (
                <div style={{ marginTop: 14, padding: 12, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, background: UI.panelAlt }}>
                  <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1, color: UI.textFnt, textTransform: "uppercase", marginBottom: 8 }}>
                    Import setlist · {importTarget}
                  </div>
                  <textarea
                    value={slImport.text}
                    onChange={e => setSlImport(p => ({ ...p, text: e.target.value }))}
                    rows={6}
                    autoFocus
                    placeholder={"Paste from Apple Notes, Google Docs, anywhere…\n\nOpener — C — 120\nSecond Song (Drop D)\nEncore: Closer"}
                    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
                  />
                  {slImport.error && <div style={{ fontFamily: FF, fontSize: 11, color: "#c46060", marginTop: 6 }}>{slImport.error}</div>}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={() => setSlImport(null)} style={ghostBtn}>Cancel</button>
                    <button onClick={doImport} disabled={!slImport.text.trim() || slImport.loading}
                      style={{ ...ghostBtn, flex: 1, background: UI.amber, color: "var(--accent-fg)", border: "none", fontWeight: 700, opacity: (!slImport.text.trim() || slImport.loading) ? 0.5 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      {slImport.loading ? "Parsing…" : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/></svg>Parse & add</>}
                    </button>
                  </div>
                </div>
              )}
            </Section>
          );
        })()}

        {/* Who's in Town — members only, when city is known */}
        {memberRole && city && memberNetworkProfiles?.length > 0 && (() => {
          const inTown = inTownDay;
          if (!inTown.length) return null;

          const inTownConnected = inTown.filter(p => memberConnections?.has(p.id));
          const inTownOthers    = inTown.filter(p => !memberConnections?.has(p.id));
          const myName          = memberOwnProfile?.preferred_name || memberOwnProfile?.name || "";

          const renderRow = (p, isLast) => (
            <NetworkPersonRow
              key={p.id} p={p} isLast={isLast} city={city} myName={myName}
              connected={memberConnections?.has(p.id)}
              onOpen={setInTownOverlay} onToggleConnect={toggleMemberConnection}
              UI={UI} compact
            />
          );

          return (
            <Section label={`Who's in Town · ${city}${inTown.length > 1 ? ` (${inTown.length})` : ""}`} UI={UI}>
              {inTownConnected.length > 0 && inTownOthers.length > 0 && (
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.amber, marginBottom: 10 }}>Connections</div>
              )}
              {inTownConnected.map((p, i) => renderRow(p, i === inTownConnected.length - 1 && inTownOthers.length === 0))}
              {inTownConnected.length > 0 && inTownOthers.length > 0 && (
                <div style={{ borderTop: `1px solid ${UI.panelBdr}`, margin: "12px 0 10px" }}>
                  <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt, marginTop: 10 }}>Others in town</div>
                </div>
              )}
              {inTownOthers.map((p, i) => renderRow(p, i === inTownOthers.length - 1))}
            </Section>
          );
        })()}

        {/* Profile overlay for who's in town taps */}
        {inTownOverlay && (
          <ProfileDetailOverlay
            person={inTownOverlay}
            onClose={() => setInTownOverlay(null)}
            lightMode={lightMode}
            UI={UI}
            memberConnections={memberConnections}
            toggleMemberConnection={toggleMemberConnection}
            memberOwnProfile={memberOwnProfile}
          />
        )}

        {/* Empty state */}
        {!hasContent && fetchDone && (
          <MobileEmpty UI={UI} title="Nothing scheduled" hint={city || undefined} pad="48px 20px" />
        )}
      </div>
    </div>
  );
}

function TourScreen({ tourDays, tour, artist, venues, lightMode, UI, memberRole,
  dayLodging, dayFlights, dayTransport, loadDayLodging, loadDayFlights, loadDayTransport,
  recentlyChanged, onDaySelect }) {
  const today = new Date().toISOString().slice(0, 10);
  const [pastExpanded, setPastExpanded] = useState(false);
  const todayRef = useRef(null);
  useEffect(() => {
    todayRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, []);

  // Split past/upcoming + group by month once per tourDays change (was recomputed
  // on every render). Runs before the early returns so hook order stays stable.
  const { pastDays, upcomingDays, pastGroups, upcomingGroups } = useMemo(() => {
    const groupByMonth = (days) => {
      const groups = {};
      days.forEach(d => {
        const m = d.date.slice(0, 7);
        (groups[m] = groups[m] || []).push(d);
      });
      return groups;
    };
    const past = tourDays.filter(d => d.date < today);
    const up   = tourDays.filter(d => d.date >= today);
    return { pastDays: past, upcomingDays: up, pastGroups: groupByMonth(past), upcomingGroups: groupByMonth(up) };
  }, [tourDays, today]);

  if (!tour) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 32px", gap: 12, textAlign: "center" }}>
        <div style={{ color: UI.textVFnt, opacity: 0.5 }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg></div>
        <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>No tour selected</div>
        <div style={{ fontFamily: FF, fontSize: 14, color: UI.textMut, lineHeight: 1.7, maxWidth: 260 }}>
          {memberRole
            ? "Your tour manager hasn't added you to a tour yet. Check back soon."
            : "Tap the ADVANCD header above to choose a tour, or open the desktop app to create one."}
        </div>
      </div>
    );
  }

  if (!tourDays.length) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, gap: 10 }}>
        <div style={{ color: UI.textVFnt, opacity: 0.5 }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
        <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.textMut, textAlign: "center" }}>
          No itinerary yet
        </div>
        <div style={{ fontFamily: FF, fontSize: 12, color: UI.textVFnt, textAlign: "center", lineHeight: 1.6, maxWidth: 240 }}>
          {memberRole
            ? "Your tour manager hasn't built the day-by-day schedule yet. Check back soon."
            : "Switch to Tour Mode on desktop to generate tour days."}
        </div>
      </div>
    );
  }

  const showCount  = tourDays.filter(d => d.type === "show").length;
  const totalDays  = tourDays.length;
  const startDate  = tourDays[0]?.date;
  const endDate    = tourDays[tourDays.length - 1]?.date;
  const dateRange  = startDate && endDate
    ? `${new Date(startDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(endDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : "";

  // Reusable row renderer
  const renderDayRow = (d) => {
    const showObj = d.show_id ? (tour?.shows || []).find(s => s.id === d.show_id) : null;
    const v       = showObj ? venues[showObj.venueId] : null;
    const isToday = d.date === today;
    const isPast  = d.date < today;
    const isShow  = d.type === "show";
    const wkday   = new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" });
    const dayNum  = new Date(d.date + "T12:00:00").getDate();

    const isFlashing = recentlyChanged?.has(d.id);
    return (
      <button key={d.id}
        className="mob-press"
        ref={isToday ? todayRef : undefined}
        onClick={() => onDaySelect?.(d)}
        style={{
          width: "100%", padding: "10px 20px",
          borderTop: "none", borderLeft: "none", borderRight: "none",
          borderBottom: `1px solid ${UI.panelBdr}`,
          display: "flex", alignItems: "center", gap: 14,
          opacity: isPast ? 0.45 : 1,
          background: isFlashing
            ? (lightMode ? "#fff0cc" : "#1a1400")
            : isToday ? (lightMode ? "#fffbee" : "#0f0e08") : "transparent",
          transition: "background 1.5s ease",
          cursor: "pointer", textAlign: "left",
          WebkitTapHighlightColor: "transparent",
        }}>
        {/* Date column */}
        <div style={{ width: 36, flexShrink: 0, textAlign: "center" }}>
          <div style={{ fontFamily: FF, fontSize: 11, color: isToday ? UI.amber : UI.textFnt, textTransform: "uppercase", letterSpacing: "0.5px" }}>{wkday}</div>
          <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: isToday ? UI.amber : UI.textMut, lineHeight: 1.1 }}>{dayNum}</div>
        </div>

        {/* Dot */}
        <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: isShow ? UI.amber : UI.panelBdr }} />

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {isShow ? (
            <>
              <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {v?.name || "Venue TBD"}
              </div>
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>
                {d.city || v?.city || ""}
              </div>
            </>
          ) : (
            <div style={{ fontFamily: FF, fontSize: 12, color: UI.textVFnt }}>
              {d.city ? d.city : "Day Off"}
            </div>
          )}
        </div>

        {/* Bus call + today badge + status dot (TM only) + chevron */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {d.bus_call && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber }}>{d.bus_call}</div>
          )}
          {isToday && (
            <div style={{
              fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
              color: UI.amber, background: UI.amdBg, padding: "2px 6px", borderRadius: 4,
            }}>Today</div>
          )}
          {!memberRole && isShow && showObj?.status && showObj.status !== "unstarted" && (
            <div title={showObj.status.replace(/_/g, " ")} style={{
              width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
              background: showObj.status === "confirmed" ? "var(--success)"
                        : showObj.status === "advancing" ? "var(--accent)"
                        : showObj.status === "dayOf"     ? "#60b8ff"
                        : UI.textVFnt,
            }} />
          )}
          <span style={{ color: UI.panelBdr, lineHeight: 1, display: "flex" }}><ChevronIcon size={13} /></span>
        </div>
      </button>
    );
  };

  // Render a set of month-grouped days
  const renderMonthGroups = (groups) =>
    Object.entries(groups).map(([month, days]) => {
      const monthLabel = new Date(month + "-01T12:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" });
      return (
        <div key={month}>
          <div style={{
            padding: "7px 20px 5px",
            background: lightMode ? "#f2ede8" : "#111",
            borderBottom: `1px solid ${UI.panelBdr}`,
          }}>
            <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt }}>{monthLabel}</span>
          </div>
          {days.map(d => renderDayRow(d))}
        </div>
      );
    });

  return (
    <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80, WebkitOverflowScrolling: "touch" }}>
      {/* Tour header */}
      <div style={{ padding: "14px 20px 12px", borderBottom: `1px solid ${UI.panelBdr}` }}>
        <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>{tour?.name}</div>
        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
          {artist?.name} · {showCount} show{showCount !== 1 ? "s" : ""} · {totalDays} days
        </div>
        {dateRange && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 3 }}>{dateRange}</div>}
      </div>

      {/* Past days — collapsed by default */}
      {pastDays.length > 0 && (
        <>
          <button
            className="mob-press"
            onClick={() => setPastExpanded(p => !p)}
            style={{
              width: "100%", padding: "10px 20px",
              borderTop: "none", borderLeft: "none", borderRight: "none",
              borderBottom: `1px solid ${UI.panelBdr}`,
              display: "flex", alignItems: "center", gap: 8,
              background: lightMode ? "#f5f2ee" : "#0e0e0e",
              cursor: "pointer", textAlign: "left",
              WebkitTapHighlightColor: "transparent",
            }}>
            <span style={{ color: UI.textVFnt, lineHeight: 1, display: "flex" }}>
              <ChevronIcon size={11} style={{ transform: pastExpanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
            </span>
            <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>
              {pastDays.length} past day{pastDays.length !== 1 ? "s" : ""}
            </span>
            <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, opacity: 0.6 }}>
              · {pastDays.filter(d => d.type === "show").length} show{pastDays.filter(d => d.type === "show").length !== 1 ? "s" : ""}
            </span>
          </button>
          {pastExpanded && renderMonthGroups(pastGroups)}
        </>
      )}

      {/* Upcoming days (today + future) — always visible */}
      {upcomingDays.length > 0
        ? renderMonthGroups(upcomingGroups)
        : (
          <MobileEmpty UI={UI} title="Tour complete" hint="All days are in the past" />
        )
      }

    </div>
  );
}

// ── Invite Member Overlay ─────────────────────────────────────────────────────
function InviteOverlay({ tourId, artistId, teamId, inviterName, tourName, artistName, onClose, lightMode, UI }) {
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [role,     setRole]     = useState("crew");
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(null); // null | { inviteUrl, emailSent }
  const [error,    setError]    = useState("");

  const handleSend = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // 1. Insert pending tour_members row (invite_token auto-generated by DB)
      const { data: row, error: insErr } = await supabase
        .from("tour_members").insert({
          team_id: teamId, tour_id: tourId, artist_id: artistId,
          email: email.trim(), name: name.trim() || null, role,
        }).select("invite_token").single();
      if (insErr) throw new Error(insErr.message);

      const inviteUrl = `${window.location.origin}/invite/${row.invite_token}`;

      // 2. Attempt to send email via API (non-blocking — fails silently if no RESEND_API_KEY)
      fetch("/api/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), role, inviteToken: row.invite_token, tourName, artistName, inviterName }),
      }).catch(() => {});

      // 3. Copy link to clipboard as fallback
      try { await navigator.clipboard.writeText(inviteUrl); } catch {}

      setDone({ inviteUrl, emailSent: true });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: UI.bg, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{
        padding: "0 20px",
        paddingTop: "calc(14px + env(safe-area-inset-top, 0px))",
        paddingBottom: 14,
        borderBottom: `1px solid ${UI.panelBdr}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>Invite Member</div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: UI.textMut, cursor: "pointer", lineHeight: 1, display: "inline-flex", alignItems: "center" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px 20px", WebkitOverflowScrolling: "touch" }}>
        {done ? (
          /* ── Success state ── */
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "center", color: UI.green }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text, marginBottom: 8 }}>Invite sent!</div>
            <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut, marginBottom: 24, lineHeight: 1.6 }}>
              An email was sent to <strong>{email}</strong>. The invite link has also been copied to your clipboard.
            </div>
            <div style={{
              fontFamily: FF, fontSize: 11, color: UI.textFnt, padding: "12px 14px",
              background: lightMode ? "#f4f0ea" : "#111", borderRadius: 8,
              wordBreak: "break-all", textAlign: "left", lineHeight: 1.6, marginBottom: 24,
            }}>{done.inviteUrl}</div>
            <button onClick={onClose}
              style={{ width: "100%", padding: 13, background: UI.amber, border: "none", borderRadius: 8, fontFamily: FF, fontSize: 14, fontWeight: 700, color: "var(--accent-fg)", cursor: "pointer" }}>
              Done
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSend}>
            {/* Role picker */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 10 }}>Role</div>
              <div style={{ display: "flex", gap: 10 }}>
                {["band", "crew"].map(r => (
                  <button key={r} type="button" onClick={() => setRole(r)}
                    style={{
                      flex: 1, padding: "11px 0",
                      background: role === r ? UI.amber : UI.panelAlt,
                      border: `1px solid ${role === r ? UI.amber : UI.panelBdr}`,
                      borderRadius: 8, cursor: "pointer",
                      fontFamily: FF, fontSize: 12, fontWeight: role === r ? 700 : 400,
                      color: role === r ? "var(--accent-fg)" : UI.text,
                      WebkitTapHighlightColor: "transparent",
                    }}>
                    {r === "band" ? "Band Member" : "Crew Member"}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 8 }}>Name</div>
              <input type="text" placeholder="Full name" value={name} onChange={e => setName(e.target.value)}
                autoCapitalize="words" autoComplete="name"
                style={{ width: "100%", padding: "11px 12px", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, color: UI.text, fontFamily: FF, fontSize: 14, outline: "none", boxSizing: "border-box" }}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 8 }}>Email</div>
              <input type="email" placeholder="email@example.com" value={email} onChange={e => setEmail(e.target.value)} required
                inputMode="email" autoCapitalize="none" autoComplete="email"
                style={{ width: "100%", padding: "11px 12px", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, color: UI.text, fontFamily: FF, fontSize: 14, outline: "none", boxSizing: "border-box" }}
              />
            </div>

            {error && <div style={{ fontFamily: FF, fontSize: 12, color: "#f87171", marginBottom: 14 }}>{error}</div>}

            <button type="submit" disabled={loading || !email.trim()}
              style={{ width: "100%", padding: 13, background: UI.amber, border: "none", borderRadius: 8, fontFamily: FF, fontSize: 14, fontWeight: 700, color: "var(--accent-fg)", cursor: "pointer", opacity: loading || !email.trim() ? 0.5 : 1 }}>
              {loading ? "Sending…" : "Send Invite"}
            </button>

            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, textAlign: "center", marginTop: 12, lineHeight: 1.6 }}>
              They'll receive an email with a sign-up link.{"\n"}The link is also copied to your clipboard.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Member Profile Screen ─────────────────────────────────────────────────────
// Shown on the People tab when the logged-in user is a band/crew member.
// Lets them set name, role, visibility, and per-day tour routing.
function MemberProfileScreen({
  memberOwnProfile, memberNetworkProfiles, memberConnections, toggleMemberConnection,
  tourDays, venues, tour, memberRole, saveMemberProfile, lightMode, UI,
}) {
  const today = new Date().toISOString().slice(0, 10);

  // Identity / photo / skills / visibility / travel info all live in the shared
  // <ProfileEditor> (same component as desktop My Profile). This screen keeps
  // only the mobile-specific bits: tour routing + connections.

  // ── Routing — built from the member's tour schedule ────────────────────────
  const buildDefaultRouting = () => {
    const existing = Array.isArray(memberOwnProfile?.tour_routing) ? memberOwnProfile.tour_routing : [];
    const existingByDate = Object.fromEntries(existing.map(r => [r.date, r]));
    return (tourDays || []).map(d => {
      const show   = d.show_id ? (tour?.shows || []).find(s => s.id === d.show_id) : null;
      const venue  = show ? venues[show.venueId] : null;
      const city   = d.city || venue?.city || "";
      const prev   = existingByDate[d.date];
      return {
        date:    d.date,
        city:    prev?.city ?? city,
        enabled: prev ? prev.enabled !== false : !!city,
        type:    d.type,
        venueName: venue?.name || "",
      };
    }).filter(r => r.city); // only days we actually have a city for
  };

  const [routing, setRouting]                 = useState(buildDefaultRouting);
  const [showPastRouting, setShowPastRouting] = useState(false);
  const [routingStatus, setRoutingStatus]     = useState("idle"); // idle | pending | saving | saved | error
  const [routingTouched, setRoutingTouched]   = useState(false);
  const routingTimerRef = useRef(null);

  // Late-load sync: profile and/or tour days can arrive after mount. Rebuild the
  // routing list only while the user hasn't edited it (fixes the old clobber race
  // that wiped in-progress edits). Render-time "state from previous render"
  // pattern per React docs — no effect needed.
  const routingSeed = `${memberOwnProfile?.id || ""}:${(tourDays || []).length}`;
  const [prevRoutingSeed, setPrevRoutingSeed] = useState(routingSeed);
  if (routingSeed !== prevRoutingSeed) {
    setPrevRoutingSeed(routingSeed);
    if (!routingTouched) setRouting(buildDefaultRouting());
  }

  const queueRoutingSave = (next) => {
    if (!memberOwnProfile?.id) return; // profile row is created via the editor first
    clearTimeout(routingTimerRef.current);
    setRoutingStatus("pending");
    routingTimerRef.current = setTimeout(async () => {
      setRoutingStatus("saving");
      const tourRouting = next.filter(r => r.enabled).map(r => ({ date: r.date, city: r.city }));
      const { error } = await saveMemberProfile({ tour_routing: tourRouting });
      setRoutingStatus(error ? "error" : "saved");
      if (!error) setTimeout(() => setRoutingStatus(s => (s === "saved" ? "idle" : s)), 2000);
    }, 900);
  };
  const updateRoutingAt = (idx, patch) => {
    setRoutingTouched(true);
    const next = routing.map((x, j) => (j === idx ? { ...x, ...patch } : x));
    setRouting(next);
    queueRoutingSave(next);
  };
  useEffect(() => () => clearTimeout(routingTimerRef.current), []);

  // ── Travel info (people_private) — member reads/writes their own row ───────
  // Requires the "own profile" RLS policies on people_private (see migration).
  const [privData, setPrivData] = useState(null);
  useEffect(() => {
    if (!memberOwnProfile?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('people_private').select('*').eq('profile_id', memberOwnProfile.id).maybeSingle();
      if (!cancelled && data) setPrivData(data);
    })();
    return () => { cancelled = true; };
  }, [memberOwnProfile?.id]);

  const saveProfilePatch = async (patch) => {
    const { error } = await saveMemberProfile(patch);
    return { error };
  };
  const savePrivatePatch = async (patch) => {
    if (!memberOwnProfile?.id) return { error: "No profile yet" };
    if (privData?.id) {
      const { error } = await supabase.from('people_private').update(patch).eq('id', privData.id);
      if (error) return { error };
      setPrivData(p => ({ ...p, ...patch }));
    } else {
      const row = { ...patch, profile_id: memberOwnProfile.id, team_id: memberOwnProfile.team_id || null };
      const { data: ins, error } = await supabase.from('people_private').insert(row).select().single();
      if (error) return { error };
      if (ins) setPrivData(ins);
    }
    return {};
  };
  const uploadPhoto = async (file) => {
    try {
      const ext  = file.name.split(".").pop();
      const path = `profiles/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("advance-files").upload(path, file, { upsert: true });
      if (error) return { error };
      const { data: urlData } = supabase.storage.from("advance-files").getPublicUrl(path);
      const { error: saveErr } = await saveMemberProfile({ photo_url: urlData.publicUrl });
      return saveErr ? { error: saveErr } : {};
    } catch (err) { return { error: err }; }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`,
    borderRadius: 8, padding: "10px 12px",
    fontFamily: FF, fontSize: 14, color: UI.text,
    outline: "none", WebkitAppearance: "none",
  };

  const dateLabel = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  // Routing rows split into upcoming vs. past — past days collapse behind a
  // disclosure so the list opens at today, not three weeks ago. Index pairs
  // preserve positions in the master `routing` array for edits.
  const routingPairs  = routing.map((r, i) => ({ r, i }));
  const upcomingRows  = routingPairs.filter(({ r }) => r.date >= today);
  const pastRows      = routingPairs.filter(({ r }) => r.date < today);

  const routingRow = ({ r, i }, isLast) => (
    <div key={r.date} style={{
      display: "flex", alignItems: "center", gap: 12,
      paddingBottom: 10, marginBottom: 10,
      borderBottom: isLast ? "none" : `1px solid ${UI.panelBdr}`,
      opacity: r.date < today ? 0.5 : 1,
    }}>
      <div style={{ width: 92, flexShrink: 0 }}>
        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{dateLabel(r.date)}</div>
        {r.venueName && r.type === "show" && (
          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.venueName}</div>
        )}
      </div>
      <input
        value={r.city}
        onChange={e => updateRoutingAt(i, { city: e.target.value })}
        placeholder="City"
        style={{ ...inputStyle, flex: 1, minWidth: 0, padding: "8px 10px", fontSize: 13 }}
      />
      <Switch size="sm" on={r.enabled} onToggle={() => updateRoutingAt(i, { enabled: !r.enabled })} UI={UI} />
    </div>
  );

  // Connections list — profiles this member has connected with
  const connectedProfiles = (memberNetworkProfiles || []).filter(p => memberConnections?.has(p.id));

  // Connections tab state
  const [connFilter,       setConnFilter]       = useState("all");
  const [connOverlayPerson, setConnOverlayPerson] = useState(null);

  // Role filter options derived from actual connections
  const connRoleOptions = (() => {
    const cats = new Set(connectedProfiles.map(p => p.role_category || "other").filter(Boolean));
    const order = ["band", "crew", "tm", "management", "production", "session", "other"];
    return ["all", ...order.filter(c => cats.has(c))];
  })();
  const ROLE_LABEL = { all: "All", band: "Band", crew: "Crew", tm: "TM", management: "Mgmt", production: "Production", session: "Session", other: "Other" };
  const CAT_COLOR  = CAT_COLORS;

  const filteredConns = connFilter === "all" ? connectedProfiles : connectedProfiles.filter(p => (p.role_category || "other") === connFilter);

  // Compute next crossing date for a given profile
  const nextCrossing = (p) => {
    const today2   = new Date().toISOString().slice(0, 10);
    const myRoute  = Array.isArray(memberOwnProfile?.tour_routing) ? memberOwnProfile.tour_routing : [];
    const theirRoute = Array.isArray(p.tour_routing) ? p.tour_routing : [];
    const theirByDate = {};
    for (const r of theirRoute) { theirByDate[r.date] = (r.city || "").toLowerCase().trim(); }
    for (const r of myRoute) {
      if (r.date < today2) continue;
      const myCity    = (r.city || "").toLowerCase().trim();
      const theirCity = theirByDate[r.date] || "";
      if (myCity && theirCity && (myCity === theirCity || myCity.startsWith(theirCity) || theirCity.startsWith(myCity))) {
        return { date: r.date, city: r.city };
      }
    }
    return null;
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80, WebkitOverflowScrolling: "touch" }}>

      {/* Header */}
      <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${UI.panelBdr}` }}>
        <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>My Profile</div>
        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
          Visible to other artists & crew in your network · saves automatically
        </div>
      </div>

      {/* Shared profile editor — identity, photo, skills, visibility, travel info.
          Same component as the desktop My Profile; auto-saves as you type. */}
      <div style={{ padding: "18px 20px", borderBottom: `1px solid ${UI.panelBdr}` }}>
        <ProfileEditor
          profile={memberOwnProfile}
          privateData={privData}
          onSaveProfile={saveProfilePatch}
          onSavePrivate={memberOwnProfile?.id ? savePrivatePatch : undefined}
          uploadPhoto={uploadPhoto}
          UI={UI}
          variant="mobile"
          rolePlaceholder={memberRole === "band" ? "e.g. Guitarist, Vocalist" : "e.g. FOH Engineer, Tour Manager"}
        />
      </div>

      {/* Tour routing */}
      <div style={{ padding: "18px 20px", borderBottom: `1px solid ${UI.panelBdr}` }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
          <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt }}>
            Tour Routing
          </div>
          {routingStatus !== "idle" && (
            <span style={{ fontFamily: FF, fontSize: 11, color: routingStatus === "error" ? UI.danger : routingStatus === "saved" ? UI.success : UI.textMut }}>
              {routingStatus === "saving" || routingStatus === "pending" ? "Saving…" : routingStatus === "saved" ? "Saved" : "Couldn't save"}
            </span>
          )}
        </div>
        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 14 }}>
          Toggle the days you want to appear in city searches. Cities are pulled from your tour schedule.
        </div>
        {routing.length === 0 && (
          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>No tour days with cities found.</div>
        )}
        {upcomingRows.map((pair, k) => routingRow(pair, k === upcomingRows.length - 1 && (pastRows.length === 0 || !showPastRouting)))}
        {pastRows.length > 0 && (
          <button
            onClick={() => setShowPastRouting(o => !o)}
            style={{
              width: "100%", padding: "9px 0", marginBottom: showPastRouting ? 10 : 0,
              background: "none", border: "none", cursor: "pointer",
              fontFamily: FF, fontSize: 11, letterSpacing: "0.5px", color: UI.textMut,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              WebkitTapHighlightColor: "transparent",
            }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={UI.textFnt} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showPastRouting ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}><polyline points="6 9 12 15 18 9" /></svg>
            {showPastRouting ? "Hide past days" : `Show ${pastRows.length} past day${pastRows.length === 1 ? "" : "s"}`}
          </button>
        )}
        {showPastRouting && pastRows.map((pair, k) => routingRow(pair, k === pastRows.length - 1))}
      </div>

      {/* My Connections */}
      {connectedProfiles.length > 0 && (
        <div style={{ padding: "18px 20px 0" }}>
          {/* Header + count */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt }}>
              My Connections
            </div>
            <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>{connectedProfiles.length}</span>
          </div>

          {/* Role filter pills */}
          {connRoleOptions.length > 2 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {connRoleOptions.map(cat => {
                const active = connFilter === cat;
                const col    = cat === "all" ? UI.textFnt : (CAT_COLOR[cat] || UI.textFnt);
                return (
                  <button key={cat} onClick={() => setConnFilter(cat)}
                    style={{
                      fontFamily: FF, fontSize: 11, fontWeight: active ? 700 : 400,
                      letterSpacing: "0.5px", padding: "4px 10px", borderRadius: 12,
                      background: active ? `${col}20` : UI.panelAlt,
                      border: `1px solid ${active ? col + "55" : UI.panelBdr}`,
                      color: active ? col : UI.textMut,
                      cursor: "pointer", WebkitTapHighlightColor: "transparent",
                      transition: "all 0.12s",
                    }}>
                    {ROLE_LABEL[cat] || cat}
                  </button>
                );
              })}
            </div>
          )}

          {/* Connection cards */}
          {filteredConns.map((p, idx) => {
            const catCol   = CAT_COLOR[p.role_category || "other"] || UI.textFnt;
            const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
            const crossing = nextCrossing(p);
            const isLast   = idx === filteredConns.length - 1;
            return (
              <button key={p.id}
                onClick={() => setConnOverlayPerson(p)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  paddingBottom: 12, marginBottom: 12,
                  borderBottom: isLast ? "none" : `1px solid ${UI.panelBdr}`,
                  background: "none", border: "none", cursor: "pointer",
                  textAlign: "left", padding: "0 0 12px 0",
                  WebkitTapHighlightColor: "transparent",
                }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                  background: `${catCol}18`, border: `2px solid ${catCol}44`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FF, fontSize: 14, fontWeight: 700, color: catCol, overflow: "hidden",
                }}>
                  {p.photo_url
                    ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.preferred_name || p.name}
                  </div>
                  {(p.role || p.role_category) && (
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>
                      {p.role || p.role_category}
                    </div>
                  )}
                  {crossing && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={catCol} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <span style={{ fontFamily: FF, fontSize: 11, color: catCol, fontWeight: 600 }}>
                        {crossing.city} · {new Date(crossing.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                  )}
                </div>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={UI.textVFnt} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            );
          })}

          {filteredConns.length === 0 && connFilter !== "all" && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, padding: "8px 0 16px" }}>
              No {ROLE_LABEL[connFilter]?.toLowerCase()} connections yet.
            </div>
          )}
        </div>
      )}

      {/* Connection detail overlay */}
      {connOverlayPerson && (
        <ProfileDetailOverlay
          person={connOverlayPerson}
          onClose={() => setConnOverlayPerson(null)}
          lightMode={lightMode}
          UI={UI}
          memberConnections={memberConnections}
          toggleMemberConnection={toggleMemberConnection}
          memberOwnProfile={memberOwnProfile}
        />
      )}
    </div>
  );
}

// ── Profile Detail Overlay ────────────────────────────────────────────────────
function ProfileDetailOverlay({ person, onClose, lightMode, UI,
  memberConnections, toggleMemberConnection, memberOwnProfile }) {
  // Swipe-down-to-dismiss (only engages when the sheet is scrolled to the top)
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef(null);
  const sheetRef  = useRef(null);
  const onSheetTouchStart = (e) => {
    dragStart.current = (sheetRef.current?.scrollTop || 0) <= 0 ? e.touches[0].clientY : null;
  };
  const onSheetTouchMove = (e) => {
    if (dragStart.current === null) return;
    const dy = e.touches[0].clientY - dragStart.current;
    if (dy > 0) setDragY(dy);
  };
  const onSheetTouchEnd = () => {
    if (dragY > 90) onClose(); else setDragY(0);
    dragStart.current = null;
  };

  if (!person) return null;
  const initials  = (person.preferred_name || person.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const catColor  = roleColor(person.role_category, UI.textFnt);
  const cleanPhone = (person.phone || "").replace(/[^+\d]/g, "");
  const isConnected = memberConnections?.has(person.id);

  // Compute upcoming city crossings between my routing and theirs
  const today = new Date().toISOString().slice(0, 10);
  const crossings = (() => {
    const myRouting   = Array.isArray(memberOwnProfile?.tour_routing) ? memberOwnProfile.tour_routing : [];
    const theirRouting = Array.isArray(person.tour_routing) ? person.tour_routing : [];
    if (!myRouting.length || !theirRouting.length) return [];
    const theirByDate = {};
    for (const r of theirRouting) { theirByDate[r.date] = (r.city || "").toLowerCase().trim(); }
    return myRouting
      .filter(r => r.date >= today)
      .reduce((acc, r) => {
        const myCity    = (r.city || "").toLowerCase().trim();
        const theirCity = theirByDate[r.date] || "";
        if (myCity && theirCity && (myCity === theirCity || myCity.startsWith(theirCity) || theirCity.startsWith(myCity))) {
          // Merge consecutive dates into ranges
          const last = acc[acc.length - 1];
          if (last && last.city === r.city && last.endDate === r.date) {
            last.endDate = r.date;
          } else if (last && last.city === r.city) {
            // gap — check if it's just 1 day apart
            const lastMs  = new Date(last.endDate || last.date).getTime();
            const thisMs  = new Date(r.date).getTime();
            if (thisMs - lastMs <= 86400000 * 2) { last.endDate = r.date; }
            else { acc.push({ date: r.date, city: r.city }); }
          } else {
            acc.push({ date: r.date, city: r.city });
          }
        }
        return acc;
      }, [])
      .slice(0, 4); // max 4 crossings shown
  })();

  const fmtCrossDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end" }}>
      <div
        ref={sheetRef}
        onClick={e => e.stopPropagation()}
        onTouchStart={onSheetTouchStart}
        onTouchMove={onSheetTouchMove}
        onTouchEnd={onSheetTouchEnd}
        style={{
          width: "100%", background: UI.bg, borderRadius: "18px 18px 0 0",
          paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
          maxHeight: "82vh", overflowY: "auto",
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: dragY === 0 ? "transform 0.2s ease" : undefined,
        }}>
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 4, background: UI.panelBdr }} />
        </div>

        {/* Avatar + name + connect */}
        <div style={{ padding: "16px 24px 20px", display: "flex", alignItems: "center", gap: 16, borderBottom: `1px solid ${UI.panelBdr}` }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${catColor}18`, border: `2px solid ${catColor}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: catColor, flexShrink: 0, overflow: "hidden" }}>
            {person.photo_url
              ? <img src={person.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text }}>{person.preferred_name || person.name}</div>
            {person.name && person.preferred_name && person.preferred_name !== person.name && (
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{person.name}</div>
            )}
            {person.role && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, marginTop: 2 }}>{person.role}</div>}
          </div>
          {/* Connect / Following button — only when toggleMemberConnection provided */}
          {toggleMemberConnection && (
            <button
              onClick={() => toggleMemberConnection(person.id)}
              style={{
                fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: "0.6px",
                padding: "7px 13px", borderRadius: 8, cursor: "pointer", flexShrink: 0,
                background: isConnected ? UI.amdBg : `${catColor}18`,
                border: `1px solid ${isConnected ? UI.amdBdr : catColor + "55"}`,
                color: isConnected ? UI.amber : catColor,
                transition: "all 0.12s", WebkitTapHighlightColor: "transparent",
              }}>
              {isConnected ? "Following" : "Connect"}
            </button>
          )}
          <button onClick={onClose} style={{ background: "none", border: "none", color: UI.textMut, fontSize: 20, cursor: "pointer", padding: "4px 8px", lineHeight: 1 }}>×</button>
        </div>

        {/* Upcoming crossings */}
        {crossings.length > 0 && (
          <div style={{ padding: "14px 24px", borderBottom: `1px solid ${UI.panelBdr}`, background: `${catColor}06` }}>
            <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: catColor, marginBottom: 10, opacity: 0.8 }}>
              Crossing paths
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {crossings.map((c, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "5px 10px", borderRadius: 8,
                  background: `${catColor}14`, border: `1px solid ${catColor}30`,
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={catColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: UI.text }}>{c.city}</span>
                  <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>
                    {c.endDate && c.endDate !== c.date
                      ? `${fmtCrossDate(c.date)} – ${fmtCrossDate(c.endDate)}`
                      : fmtCrossDate(c.date)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact actions */}
        {(person.phone || person.email) && (
          <div style={{ padding: "16px 24px", display: "flex", gap: 10, borderBottom: `1px solid ${UI.panelBdr}` }}>
            {person.phone && (
              <a href={`tel:${cleanPhone}`}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "12px 8px", background: UI.panelAlt, borderRadius: 8, border: `1px solid ${UI.panelBdr}`, textDecoration: "none", WebkitTapHighlightColor: "transparent" }}>
                <span style={{ opacity: 0.8 }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.68 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.49 5.49l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg></span>
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.text, fontWeight: 600 }}>Call</span>
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{fmtPhone(person.phone)}</span>
              </a>
            )}
            {person.phone && (
              <a href={`sms:${cleanPhone}`}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "12px 8px", background: UI.panelAlt, borderRadius: 8, border: `1px solid ${UI.panelBdr}`, textDecoration: "none", WebkitTapHighlightColor: "transparent" }}>
                <span style={{ opacity: 0.8 }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.text, fontWeight: 600 }}>Message</span>
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{fmtPhone(person.phone)}</span>
              </a>
            )}
            {person.email && (
              <a href={`mailto:${person.email}`}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "12px 8px", background: UI.panelAlt, borderRadius: 8, border: `1px solid ${UI.panelBdr}`, textDecoration: "none", WebkitTapHighlightColor: "transparent" }}>
                <span style={{ opacity: 0.8 }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></span>
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.text, fontWeight: 600 }}>Email</span>
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{person.email}</span>
              </a>
            )}
          </div>
        )}

        {/* Bio / details */}
        <div style={{ padding: "16px 24px" }}>
          {(person.city || person.state) && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>Based in</span>
              <span style={{ fontFamily: FF, fontSize: 11, color: UI.text, fontWeight: 500 }}>
                {[person.city, person.state].filter(Boolean).join(", ")}
              </span>
            </div>
          )}
          {person.instagram && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>Instagram</span>
              <a href={`https://instagram.com/${person.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                style={{ fontFamily: FF, fontSize: 11, color: UI.amber, textDecoration: "none" }}>
                @{person.instagram.replace(/^@/, "")}
              </a>
            </div>
          )}
          {person.bio && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 6 }}>Bio</div>
              <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, lineHeight: 1.7 }}>{person.bio}</div>
            </div>
          )}
          {!person.city && !person.instagram && !person.bio && !person.phone && !person.email && (
            <div style={{ textAlign: "center", padding: "16px 0", fontFamily: FF, fontSize: 12, color: UI.textVFnt }}>No additional info</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── People Screen ─────────────────────────────────────────────────────────────
function PeopleScreen({ profiles, tour, artist, teamId, currentUser, memberRole, lightMode, UI }) {
  const [showInvite, setShowInvite] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);

  // PP-1 Phase 2: single source of truth — people_profiles where current_tour_id = tour.id
  const tourProfiles = (profiles || []).filter(p => p.current_tour_id === tour?.id);
  const totalCount = tourProfiles.length;
  const canInvite = !memberRole; // Only TMs can send invites

  // Invite status badge for a profile
  const inviteStatusBadge = (p) => {
    if (p.claimed_at || p.user_id) {
      return { label: "App Access ✓", color: UI.green, bg: UI.ckOnBg };
    }
    if (p.invite_token) {
      return { label: "Invited", color: UI.amber, bg: UI.amdBg };
    }
    return { label: "No App Access", color: UI.textFnt, bg: lightMode ? "#e8e4de" : "#1a1a1a" };
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80, WebkitOverflowScrolling: "touch" }}>
      <div style={{ padding: "14px 20px 12px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>Touring Party</div>
          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>{totalCount} member{totalCount !== 1 ? "s" : ""}</div>
        </div>
        {canInvite && (
          <button onClick={() => setShowInvite(true)}
            style={{
              background: UI.amber, border: "none", borderRadius: 16,
              padding: "7px 16px", cursor: "pointer",
              fontFamily: FF, fontSize: 11, fontWeight: 700, color: "var(--accent-fg)",
              WebkitTapHighlightColor: "transparent",
            }}>
            + Invite
          </button>
        )}
      </div>

      {showInvite && canInvite && (
        <InviteOverlay
          tourId={tour?.id}
          artistId={artist?.id}
          teamId={teamId}
          tourName={tour?.name}
          artistName={artist?.name}
          inviterName={currentUser?.email}
          onClose={() => setShowInvite(false)}
          lightMode={lightMode} UI={UI}
        />
      )}

      {/* ── People profiles (from desktop Touring Party) ── */}
      {tourProfiles.length > 0 && (
        <>
          {tourProfiles.map(p => {
            const badge = inviteStatusBadge(p);
            const cleanPhone = (p.phone || "").replace(/[^+\d]/g, "");
            return (
              <div key={p.id}
                onClick={() => setSelectedPerson(p)}
                style={{
                  padding: "13px 20px", borderBottom: `1px solid ${UI.panelBdr}`,
                  display: "flex", alignItems: "center", gap: 14,
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                  transition: "background 0.1s",
                }}
                onTouchStart={e => e.currentTarget.style.background = lightMode ? "#ece8e3" : "#161616"}
                onTouchEnd={e => e.currentTarget.style.background = "transparent"}>
                <div style={{
                  width: 38, height: 38, borderRadius: "50%",
                  background: lightMode ? "#e8e4de" : "#1a1a1a",
                  border: `1px solid ${UI.panelBdr}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, fontWeight: 700, color: UI.amber, flexShrink: 0, overflow: "hidden",
                }}>
                  {p.photo_url
                    ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : (p.preferred_name || p.name || "?").charAt(0).toUpperCase()
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text }}>{p.preferred_name || p.name}</div>
                  {p.role && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>{p.role}</div>}
                  <span style={{
                    display: "inline-block", marginTop: 4,
                    fontFamily: FF, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase",
                    color: badge.color, background: badge.bg, padding: "2px 6px", borderRadius: 4,
                  }}>{badge.label}</span>
                </div>
                {p.phone && (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <a href={`tel:${cleanPhone}`}
                      onClick={e => e.stopPropagation()}
                      style={{ display: "flex", alignItems: "center", gap: 4, color: UI.amber, textDecoration: "none", padding: "5px 10px", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, WebkitTapHighlightColor: "transparent" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.68 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.49 5.49l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      <span style={{ fontFamily: FF, fontSize: 11 }}>Call</span>
                    </a>
                    <a href={`sms:${cleanPhone}`}
                      onClick={e => e.stopPropagation()}
                      style={{ display: "flex", alignItems: "center", gap: 4, color: UI.amber, textDecoration: "none", padding: "5px 10px", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, WebkitTapHighlightColor: "transparent" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <span style={{ fontFamily: FF, fontSize: 11 }}>Msg</span>
                    </a>
                  </div>
                )}
                <span style={{ color: UI.panelBdr, lineHeight: 1, flexShrink: 0, display: "flex" }}><ChevronIcon size={14} /></span>
              </div>
            );
          })}
        </>
      )}

      {/* PP-1 Phase 2: crew_members table retired — all touring party from people_profiles */}

      {totalCount === 0 && (
        <MobileEmpty UI={UI} title="No touring party added yet"
          hint="Add members in the Touring Party section on desktop" />
      )}

      {/* Invite flow explanation — TM only */}
      {canInvite && totalCount > 0 && (
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${UI.panelBdr}`, marginTop: 8 }}>
          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, lineHeight: 1.7 }}>
            <span style={{ color: UI.amber }}>App Access ✓</span> — member has claimed their profile and can log in to ADVANCD.
            {" "}<span style={{ color: UI.amber }}>Invited</span> — invite email sent; awaiting acceptance.
            {" "}<span style={{ color: UI.textMut }}>No App Access</span> — not yet invited. Use <span style={{ color: UI.amber }}>+ Invite</span> to send them an invite email.
          </div>
        </div>
      )}

      {selectedPerson && (
        <ProfileDetailOverlay
          person={selectedPerson}
          onClose={() => setSelectedPerson(null)}
          lightMode={lightMode}
          UI={UI}
        />
      )}
    </div>
  );
}

// ── More Screen ───────────────────────────────────────────────────────────────
function MoreScreen({ artist, tour, lightMode, UI, onSwitchTour, saved, onToggleTheme, memberRole, onSignOut }) {
  const menuItems = [
    ...(!memberRole ? [{ label: "Switch Tour / Artist", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>, action: onSwitchTour }] : []),
    { label: lightMode ? "Switch to Dark Mode" : "Switch to Light Mode",
      icon: lightMode
        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>,
      action: onToggleTheme },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80, WebkitOverflowScrolling: "touch" }}>
      {/* Current context */}
      <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${UI.panelBdr}` }}>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 6 }}>
          {memberRole ? (memberRole === "band" ? "Band Member" : "Crew Member") : "Tour Manager"}
        </div>
        <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: UI.text }}>{artist?.name || "—"}</div>
        {tour && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut, marginTop: 2 }}>{tour.name}</div>}
      </div>

      {/* Menu items */}
      {menuItems.map(item => (
        <button key={item.label} className="mob-press" onClick={item.action}
          style={{
            width: "100%", padding: "16px 20px",
            borderTop: "none", borderLeft: "none", borderRight: "none",
            borderBottom: `1px solid ${UI.panelBdr}`,
            display: "flex", alignItems: "center", gap: 16,
            background: "none", cursor: "pointer", textAlign: "left",
            WebkitTapHighlightColor: "transparent",
          }}>
          <span style={{ color: UI.textFnt, width: 24, display: "inline-flex", justifyContent: "center" }}>{item.icon}</span>
          <span style={{ fontFamily: FF, fontSize: 14, color: UI.text, flex: 1 }}>{item.label}</span>
          <span style={{ color: UI.textVFnt, display: "flex" }}><ChevronIcon size={14} /></span>
        </button>
      ))}

      {/* Save status — TM only; fades in briefly after a save, invisible otherwise */}
      {!memberRole && (
        <div style={{
          padding: "14px 20px", borderBottom: `1px solid ${UI.panelBdr}`,
          opacity: saved ? 1 : 0, transition: "opacity 0.5s", pointerEvents: "none",
        }}>
          <span style={{ fontFamily: FF, fontSize: 12, color: UI.green, display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>All changes saved</span>
        </div>
      )}

      {/* Sign out */}
      <button onClick={onSignOut} className="mob-press"
        style={{
          width: "100%", padding: "16px 20px",
          borderTop: "none", borderLeft: "none", borderRight: "none",
          borderBottom: `1px solid ${UI.panelBdr}`,
          display: "flex", alignItems: "center", gap: 16,
          background: "none", cursor: "pointer", textAlign: "left",
          WebkitTapHighlightColor: "transparent",
        }}>
        <span style={{ color: UI.textFnt, width: 24, display: "inline-flex", justifyContent: "center" }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
        <span style={{ fontFamily: FF, fontSize: 14, color: UI.text, flex: 1 }}>Sign Out</span>
        <span style={{ color: UI.textVFnt, display: "flex" }}><ChevronIcon size={14} /></span>
      </button>

      {/* Wordmark */}
      <div style={{ padding: "32px 20px", textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 11, letterSpacing: "4px", textTransform: "uppercase", color: UI.textVFnt }}>ADVANCD</div>
        <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 4, letterSpacing: "1px" }}>Tour Management</div>
      </div>
    </div>
  );
}

// ── Tour / Artist Selector ────────────────────────────────────────────────────
function TourSelectorOverlay({ artists, selA, selT, onSelect, onClose, lightMode, UI }) {
  const hasTours = artists.some(a => a.tours && a.tours.length > 0);

  // Swipe-down-to-dismiss (only when the inner list is scrolled to the top)
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef(null);
  const scrollRef = useRef(null);
  const onSheetTouchStart = (e) => {
    dragStart.current = (scrollRef.current?.scrollTop || 0) <= 0 ? e.touches[0].clientY : null;
  };
  const onSheetTouchMove = (e) => {
    if (dragStart.current === null) return;
    const dy = e.touches[0].clientY - dragStart.current;
    if (dy > 0) setDragY(dy);
  };
  const onSheetTouchEnd = () => {
    if (dragY > 90) onClose(); else setDragY(0);
    dragStart.current = null;
  };

  const getTourDateRange = (shows) => {
    if (!shows || shows.length === 0) return null;
    const dates = shows.map(s => s.date).filter(Boolean).sort();
    if (dates.length === 0) return null;
    const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (dates.length === 1) return fmt(dates[0]);
    const y0 = new Date(dates[0] + "T12:00:00").getFullYear();
    const y1 = new Date(dates[dates.length - 1] + "T12:00:00").getFullYear();
    return y0 === y1 ? `${fmt(dates[0])} – ${fmt(dates[dates.length - 1])}` : `${fmt(dates[0])} ${y0} – ${fmt(dates[dates.length - 1])} ${y1}`;
  };

  return (
    <>
      {/* Scrim */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 299,
        background: "rgba(0,0,0,0.48)",
      }} />

      {/* Bottom sheet */}
      <div
        onTouchStart={onSheetTouchStart}
        onTouchMove={onSheetTouchMove}
        onTouchEnd={onSheetTouchEnd}
        style={{
        position: "fixed", left: 0, right: 0, bottom: 0,
        zIndex: 300,
        background: lightMode ? "#faf8f5" : "#1c1c1e",
        borderRadius: "18px 18px 0 0",
        maxHeight: "78vh",
        display: "flex", flexDirection: "column",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -6px 40px rgba(0,0,0,0.3)",
        transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
        transition: dragY === 0 ? "transform 0.2s ease" : undefined,
      }}>
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}>
          <div style={{ width: 38, height: 4, borderRadius: 4, background: lightMode ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.18)" }} />
        </div>

        {/* Header */}
        <div style={{
          padding: "12px 20px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text, letterSpacing: "-0.3px" }}>Switch Tour</div>
          <button onClick={onClose} style={{
            background: lightMode ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.1)",
            border: "none", borderRadius: "50%",
            width: 30, height: 30,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: UI.textMut, cursor: "pointer",
            WebkitTapHighlightColor: "transparent", flexShrink: 0,
          }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        </div>

        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 16px 32px" }}>
          {/* Empty state */}
          {(!artists.length || !hasTours) && (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: "40px 20px 24px", gap: 14, textAlign: "center",
            }}>
              <div style={{ color: UI.textVFnt, opacity: 0.5 }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
              <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>No tours yet</div>
              <div style={{ fontFamily: FF, fontSize: 14, color: UI.textMut, lineHeight: 1.7, maxWidth: 260 }}>
                Tours are created in the ADVANCD desktop app. Open it on your computer to add an artist and build your first tour.
              </div>
              <button onClick={onClose} style={{
                marginTop: 8, fontFamily: FF, fontSize: 12, fontWeight: 600, letterSpacing: "1px",
                color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`,
                borderRadius: 8, padding: "10px 24px", cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
              }}>Got it</button>
            </div>
          )}

          {/* Artist groups + tour cards */}
          {hasTours && artists.filter(a => a.tours && a.tours.length > 0).map(a => (
            <div key={a.id} style={{ marginBottom: 24 }}>
              {/* Artist label */}
              <div style={{
                fontFamily: FF, fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase",
                color: UI.amber, marginBottom: 10, paddingLeft: 2,
              }}>{a.name}</div>

              {/* Tour cards */}
              {a.tours.map(t => {
                const isActive = t.id === selT && a.id === selA;
                const showCount = (t.shows || []).length;
                const confirmedCount = (t.shows || []).filter(s => s.status === "confirmed" || s.status === "dayOf").length;
                const dateRange = getTourDateRange(t.shows);

                return (
                  <button key={t.id} className="mob-press" onClick={() => onSelect(a.id, t.id)}
                    style={{
                      width: "100%", marginBottom: 10,
                      background: isActive
                        ? (lightMode ? "#fffbee" : "#201c00")
                        : (lightMode ? "#fff" : "#2a2a2c"),
                      border: isActive
                        ? `1.5px solid ${UI.amber}`
                        : `1.5px solid ${lightMode ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.07)"}`,
                      borderRadius: 12,
                      padding: "15px 16px",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      cursor: "pointer", textAlign: "left",
                      WebkitTapHighlightColor: "transparent",
                      boxShadow: isActive
                        ? `0 0 0 3px ${lightMode ? "rgba(212,160,30,0.12)" : "rgba(212,160,30,0.18)"}`
                        : "none",
                    }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                      <div style={{
                        fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        marginBottom: 5,
                      }}>{t.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {dateRange && (
                          <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{dateRange}</span>
                        )}
                        {dateRange && showCount > 0 && (
                          <span style={{ width: 2, height: 2, borderRadius: "50%", background: UI.textVFnt, flexShrink: 0 }} />
                        )}
                        {showCount > 0 && (
                          <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>
                            {showCount} show{showCount !== 1 ? "s" : ""}
                            {confirmedCount > 0 && (
                              <span style={{ color: UI.green }}> · {confirmedCount} confirmed</span>
                            )}
                          </span>
                        )}
                        {showCount === 0 && (
                          <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>No shows yet</span>
                        )}
                      </div>
                    </div>
                    {isActive ? (
                      <span style={{
                        fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
                        color: UI.amber, background: UI.amdBg,
                        border: `1px solid ${UI.amdBdr}`,
                        padding: "4px 10px", borderRadius: 4,
                        fontWeight: 700, flexShrink: 0,
                      }}>Active</span>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, opacity: 0.25 }}>
                        <path d="M6 3l5 5-5 5" stroke={UI.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Schedule Screen ───────────────────────────────────────────────────────────
// Dedicated daily calendar view. Merges ROS (from show advance data) with
// TM-created day_schedule_items. Members see only items assigned to them (or
// unassigned items visible to all). TMs see everything.

// Convert minutes-since-midnight → "H:MM AM/PM"
function fmtMins(m) {
  if (m == null || m < 0) return "—";
  const wrapped = m % (24 * 60); // handle next-day times (≥ 1440)
  const h24 = Math.floor(wrapped / 60);
  const min = wrapped % 60;
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h = h24 % 12 || 12;
  return `${h}:${String(min).padStart(2, "0")} ${suffix}`;
}

const SCHED_TYPE_LABELS = {
  production: "Production",
  press:      "Press",
  catering:   "Catering",
  custom:     "Event",
};
const SCHED_TYPE_COLORS = {
  production: "#5b8dee",
  press:      "#c084fc",
  catering:   "#34d399",
  custom:     "#94a3b8",
};

function ScheduleScreen({
  tourDays, tour, venues, dayScheduleItems,
  profiles, memberRole, memberOwnProfile,
  lightMode, UI,
}) {
  // Default to today's index in tourDays
  const todayStr = new Date().toISOString().slice(0, 10);
  const defaultIdx = (() => {
    const idx = tourDays.findIndex(d => d.date === todayStr);
    return idx >= 0 ? idx : 0;
  })();

  const [dayIdx, setDayIdx] = useState(defaultIdx);

  // Scroll to today on mount
  const todayRowRef = useRef(null);
  useEffect(() => {
    if (todayRowRef.current) {
      todayRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const day = tourDays[dayIdx];

  // Curated schedule items for this day, filtered per-member and sorted by time.
  // Memoized so it doesn't re-filter/re-map/re-sort on every render. Computed
  // before the early return to keep hook order stable.
  const allItems = useMemo(() => {
    if (!day) return [];
    const myPid = memberOwnProfile?.id;
    return (dayScheduleItems || [])
      .filter(x => x.tour_day_id === day.id)
      .filter(x => {
        if (!memberRole) return true; // TM preview sees all
        const ids = Array.isArray(x.assigned_profile_ids) ? x.assigned_profile_ids : [];
        return ids.length === 0 || (myPid && ids.includes(myPid));
      })
      .map(x => ({
        id: x.id,
        title: x.title,
        timeLabel: fmtMins(x.start_minutes),
        mins: x.start_minutes,
        durationMins: x.duration_minutes,
        type: x.type || "custom",
        assigned_profile_ids: Array.isArray(x.assigned_profile_ids) ? x.assigned_profile_ids : [],
        notes: x.notes || "",
      }))
      .sort((a, b) => a.mins - b.mins);
  }, [dayScheduleItems, day?.id, memberRole, memberOwnProfile?.id]);

  if (!day) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: FF, fontSize: 14, color: UI.textMut }}>No tour days loaded.</span>
      </div>
    );
  }

  const isToday = day.date === todayStr;
  const dateLabel = new Date(day.date + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long", month: "short", day: "numeric",
  });

  const show = day.show_id ? (tour?.shows || []).find(s => s.id === day.show_id) : null;
  const venue = show ? venues[show.venueId] : null;
  const city = venue?.city || day.city || "";

  const isOff = day.type !== "show" && day.type !== "travel";

  return (
    <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80, WebkitOverflowScrolling: "touch" }}>

      {/* Day navigation header */}
      <div style={{
        padding: "16px 20px 14px", borderBottom: `1px solid ${UI.panelBdr}`,
        display: "flex", alignItems: "center", gap: 8,
        background: isToday ? (lightMode ? "#fffdf5" : "#0f0e08") : UI.bg,
        flexShrink: 0,
      }}>
        <button
          onClick={() => setDayIdx(i => Math.max(0, i - 1))}
          disabled={dayIdx === 0}
          aria-label="Previous day"
          style={{
            background: "none", border: "none",
            color: dayIdx === 0 ? UI.panelBdr : UI.textMut,
            cursor: dayIdx === 0 ? "default" : "pointer",
            padding: "10px 12px", margin: "-6px 0", lineHeight: 1, WebkitTapHighlightColor: "transparent",
          }}><ChevronIcon dir="left" size={16} /></button>

        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{
            fontFamily: FF, fontSize: 11, letterSpacing: "2px",
            textTransform: "uppercase",
            color: isToday ? UI.amber : UI.textFnt,
            marginBottom: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            {isToday ? "Today · " : ""}{dateLabel}
            {day.type === "show" && (
              <span style={{
                fontSize: 11, letterSpacing: "1.5px",
                color: UI.amber, background: UI.amdBg,
                padding: "1px 6px", borderRadius: 4,
              }}>SHOW</span>
            )}
            {day.type === "travel" && (
              <span style={{
                fontSize: 11, letterSpacing: "1.5px",
                color: UI.textFnt, background: UI.panelAlt,
                padding: "1px 6px", borderRadius: 4,
              }}>TRAVEL</span>
            )}
          </div>
          <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text, lineHeight: 1.1 }}>
            {venue?.name || city || (isOff ? "Day Off" : day.type === "travel" ? "Travel Day" : "Show Day")}
          </div>
          {venue?.name && city && city !== venue.name && (
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 3 }}>{city}</div>
          )}
        </div>

        <button
          onClick={() => setDayIdx(i => Math.min(tourDays.length - 1, i + 1))}
          disabled={dayIdx === tourDays.length - 1}
          aria-label="Next day"
          style={{
            background: "none", border: "none",
            color: dayIdx === tourDays.length - 1 ? UI.panelBdr : UI.textMut,
            cursor: dayIdx === tourDays.length - 1 ? "default" : "pointer",
            padding: "10px 12px", margin: "-6px 0", lineHeight: 1, WebkitTapHighlightColor: "transparent",
          }}><ChevronIcon size={16} /></button>
      </div>

      {/* Empty state */}
      {allItems.length === 0 && (
        <MobileEmpty UI={UI} pad="32px 20px"
          title={day.type === "travel"
            ? "Travel day — no scheduled events"
            : isOff
            ? "Day off — nothing scheduled"
            : "No schedule items yet"} />
      )}

      {/* Schedule rows */}
      {allItems.map((item, idx) => {
        const isRos = item.type === "ros";
        const typeColor = isRos ? UI.amber : (SCHED_TYPE_COLORS[item.type] || SCHED_TYPE_COLORS.custom);
        const typeBg = isRos
          ? UI.amdBg
          : lightMode
          ? `${typeColor}18`
          : `${typeColor}22`;
        const typeLabel = isRos ? "ROS" : (SCHED_TYPE_LABELS[item.type] || "Event");

        // Assigned people (custom items only)
        const assignedPeople = !isRos && item.assigned_profile_ids.length > 0
          ? item.assigned_profile_ids
              .map(pid => (profiles || []).find(p => p.id === pid))
              .filter(Boolean)
          : [];

        return (
          <div key={item.id} style={{
            display: "flex", alignItems: "flex-start", gap: 14,
            padding: "13px 20px",
            borderBottom: `1px solid ${UI.panelBdr}`,
            background: idx % 2 === 0 ? "transparent" : (lightMode ? "#00000005" : "#ffffff03"),
          }}>
            {/* Time column */}
            <div style={{
              flexShrink: 0, width: 62, textAlign: "left",
              paddingTop: 2,
            }}>
              <div style={{
                fontFamily: FF, fontSize: 11, fontWeight: 700,
                color: isRos ? UI.amber : UI.text, letterSpacing: "0.3px",
              }}>{item.timeLabel}</div>
              {item.durationMins && (
                <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
                  {item.durationMins >= 60
                    ? `${Math.floor(item.durationMins / 60)}h${item.durationMins % 60 ? `${item.durationMins % 60}m` : ""}`
                    : `${item.durationMins}m`}
                </div>
              )}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{
                  fontFamily: FF, fontSize: 14, fontWeight: 600,
                  color: UI.text, lineHeight: 1.3,
                }}>{item.title}</span>
                <span style={{
                  fontFamily: FF, fontSize: 11, letterSpacing: "1px",
                  textTransform: "uppercase", fontWeight: 700,
                  color: isRos ? UI.amber : typeColor,
                  background: typeBg,
                  border: `1px solid ${isRos ? UI.amdBdr : `${typeColor}44`}`,
                  padding: "2px 6px", borderRadius: 4, flexShrink: 0,
                }}>{typeLabel}</span>
              </div>

              {/* Notes */}
              {item.notes && (
                <div style={{
                  fontFamily: FF, fontSize: 11, color: UI.textMut,
                  marginTop: 4, lineHeight: 1.4,
                }}>{item.notes}</div>
              )}

              {/* Assigned people */}
              {assignedPeople.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 5 }}>
                  {assignedPeople.slice(0, 5).map(p => (
                    <div key={p.id} title={p.preferred_name || p.name} style={{
                      width: 20, height: 20, borderRadius: "50%",
                      background: UI.panelAlt,
                      border: `1px solid ${UI.panelBdr}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, overflow: "hidden",
                    }}>
                      {p.photo_url ? (
                        <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: UI.textFnt }}>
                          {(p.preferred_name || p.name || "?")[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                  ))}
                  {assignedPeople.length > 5 && (
                    <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>
                      +{assignedPeople.length - 5}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Day mini-strip — all days as small pills for quick jumping */}
      <div style={{
        padding: "16px 20px 8px",
        borderTop: allItems.length > 0 ? `1px solid ${UI.panelBdr}` : "none",
      }}>
        <div style={{
          fontFamily: FF, fontSize: 11, letterSpacing: "2px",
          textTransform: "uppercase", color: UI.textFnt, marginBottom: 10,
        }}>Jump to day</div>
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 6,
        }}>
          {tourDays.map((d, i) => {
            const isActive = i === dayIdx;
            const isTdy = d.date === todayStr;
            const dt = new Date(d.date + "T12:00:00");
            const lbl = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            return (
              <button
                key={d.id}
                ref={isTdy ? todayRowRef : undefined}
                onClick={() => setDayIdx(i)}
                style={{
                  fontFamily: FF, fontSize: 11, fontWeight: isActive ? 700 : 400,
                  padding: "4px 9px", borderRadius: 4,
                  background: isActive ? UI.amber : (isTdy ? UI.amdBg : UI.panelAlt),
                  color: isActive ? "var(--accent-fg)" : (isTdy ? UI.amber : UI.textMut),
                  border: `1px solid ${isActive ? UI.amber : (isTdy ? UI.amdBdr : UI.panelBdr)}`,
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                }}>
                {lbl}{d.type === "show" ? " ·" : ""}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── SR-1: Post-Show Report Modal ───────────────────────────────────────────────
function ShowReportModal({ show, tourId, profileId, roleCategory, existingReport, onSave, onClose, lightMode }) {
  const [form,   setForm]   = useState(existingReport?.responses || {});
  const [saving, setSaving] = useState(false);
  const [done,   setDone]   = useState(false);

  const isBand   = roleCategory === 'band';
  const purple   = "#7c3aed";
  const amber    = "#f0a500";
  const txtColor = lightMode ? "#111" : "#eee";
  const mutColor = lightMode ? "#666" : "#888";
  const bdrColor = lightMode ? "#e0e0e0" : "#2a2a2a";
  const bgColor  = lightMode ? "#fff" : "#111";
  const cardBg   = lightMode ? "#f6f6f6" : "#1a1a1a";

  const handleSubmit = async () => {
    setSaving(true);
    const result = await onSave({ showId: show?.id, tourId, profileId, roleCategory, responses: form });
    setSaving(false);
    if (result !== false) { setDone(true); setTimeout(() => onClose(), 1200); }
  };

  const starRow = (label, key) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: FF, fontSize: 14, color: mutColor, marginBottom: 10 }}>{label}</div>
      <div style={{ display: "flex", gap: 10 }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => setForm(f => ({ ...f, [key]: f[key] === n ? null : n }))}
            style={{ width: 48, height: 48, borderRadius: 8, border: "none", cursor: "pointer",
              fontSize: 16, fontWeight: 700,
              background: (form[key] || 0) >= n ? amber : (lightMode ? "#eee" : "#2a2a2a"),
              color: (form[key] || 0) >= n ? "#fff" : mutColor }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  const pillRow = (label, key, options) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: FF, fontSize: 14, color: mutColor, marginBottom: 10 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {options.map(o => (
          <button key={o} onClick={() => setForm(f => ({ ...f, [key]: f[key] === o ? null : o }))}
            style={{ fontFamily: FF, fontSize: 14, padding: "9px 18px", borderRadius: 16,
              border: `1.5px solid ${form[key] === o ? purple : bdrColor}`,
              background: form[key] === o ? purple + "18" : "transparent",
              color: form[key] === o ? purple : mutColor, cursor: "pointer" }}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );

  const textArea = (label, key, placeholder) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: FF, fontSize: 14, color: mutColor, marginBottom: 8 }}>{label}</div>
      <textarea value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder || ""}
        style={{ width: "100%", minHeight: 80, fontFamily: FF, fontSize: 14, color: txtColor,
          background: cardBg, border: `1px solid ${bdrColor}`, borderRadius: 8,
          padding: "10px 12px", resize: "vertical", boxSizing: "border-box", outline: "none" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.65)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div style={{ background: bgColor, borderRadius: "18px 18px 0 0", padding: "20px 20px 48px", maxHeight: "92vh", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: txtColor }}>Post-Show Report</div>
            <div style={{ fontFamily: FF, fontSize: 14, color: mutColor, marginTop: 3 }}>{isBand ? "Band feedback" : "Crew feedback"}</div>
          </div>
          <button onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: 16, border: "none", background: lightMode ? "#eee" : "#2a2a2a", color: mutColor, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {isBand ? (
          <>
            {starRow("How was the show overall?", "overall_rating")}
            {pillRow("Monitor mix quality", "monitor_mix", ["Excellent", "Good", "Fair", "Poor"])}
            {pillRow("Crowd energy", "crowd_energy", ["Electric", "Great", "Decent", "Tough"])}
            {textArea("What went wrong?", "went_wrong", "Anything to flag for next time…")}
            {textArea("What went well?", "went_well", "Highlights worth noting…")}
            {pillRow("Would you play here again?", "play_again", ["Yes", "Maybe", "No"])}
            {textArea("Any other notes?", "notes", "Open notes…")}
          </>
        ) : (
          <>
            {starRow("How was the venue overall?", "overall_rating")}
            {pillRow("Load-in ease", "load_in_ease", ["Easy", "Fine", "Difficult"])}
            {pillRow("Production staff helpfulness", "staff_helpfulness", ["Very helpful", "OK", "Not helpful"])}
            {textArea("Anything broken or missing?", "issues", "Equipment issues, missing gear…")}
            {textArea("Notes for next time?", "notes", "What would make load-in smoother?")}
          </>
        )}

        <button onClick={handleSubmit} disabled={saving || done}
          style={{ width: "100%", padding: "16px", borderRadius: 12, border: "none", cursor: "pointer",
            background: done ? "var(--success)" : purple, color: "#fff",
            fontFamily: FF, fontSize: 16, fontWeight: 700, marginTop: 4 }}>
          {saving ? "Submitting…" : done ? "Submitted!" : existingReport ? "Update Report" : "Submit Report"}
        </button>
      </div>
    </div>
  );
}

// ── Mobile App Shell ──────────────────────────────────────────────────────────
export default function MobileApp({
  data, tourDays, tour, artist, venues,
  teamMembers, profiles,
  dayWeather, saved, lightMode,
  selA, selT, selS,
  setSelA, setSelT, setSelS,
  setLightMode, updShow, teamId, memberRole, memberModules, UI,
  dayLodging, dayFlights, dayTransport,
  loadDayLodging, loadDayFlights, loadDayTransport, loadDayWeather,
  user,
  memberOwnProfile, memberNetworkProfiles, memberConnections, toggleMemberConnection, saveMemberProfile,
  realtimeStatus,
  memberDataStale,
  onRefresh,
  cityRecs,
  loadCommunityRecs,
  memberPermissions,
  dayScheduleItems,
  showSetlists, saveShowSetlist, deleteShowSetlist,
  showReports = [], onSaveShowReport,
  broadcasts = [], broadcastReads = {}, markBroadcastRead,
  onChatExecuteAction = null, // TM-on-mobile only — enables chat write actions
  constrainWidth = false,
}) {
  const artists = data?.artists || [];

  // Smart default tab: Today if show within 7 days, else Tour overview
  const defaultTab = (() => {
    const today = new Date().toISOString().slice(0, 10);
    const hasNearShow = tourDays.some(d => {
      const diff = (new Date(d.date + "T12:00:00") - new Date(today + "T12:00:00")) / 86400000;
      return diff >= 0 && diff <= 7 && d.type === "show";
    });
    return hasNearShow ? "today" : "tour";
  });

  const [tab, setTab]                   = useState(defaultTab);
  // B7: the smart landing tab is computed once at mount. For invited members
  // tourDays often arrives after first render, so the "show within 7 days →
  // Today" logic runs against an empty array and lands on Tour. Recompute once
  // data arrives — but only if the user hasn't already navigated themselves.
  const userPickedTab   = useRef(false);
  const tabSettledFromData = useRef(tourDays.length > 0);
  useEffect(() => {
    if (userPickedTab.current || tabSettledFromData.current) return;
    if (tourDays.length > 0) {
      tabSettledFromData.current = true;
      setTab(defaultTab());
    }
  // defaultTab is a stable closure recomputed from current tourDays; safe to omit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourDays]);
  const [showSelector, setShowSelector]  = useState(false);
  const [showDetail, setShowDetail]      = useState(false);
  const [selectedDay, setSelectedDay]    = useState(null);

  // SR-1: Show report modal + dismissal tracking
  const [showReportModal, setShowReportModal] = useState(null); // null | { show, tourId, profileId, roleCategory, existingReport }
  const [reportDismissedShows, setReportDismissedShows] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('_rpt_dismissed') || '[]'); } catch { return []; }
  });
  const dismissReport = (showId) => {
    setReportDismissedShows(prev => {
      const next = [...prev, showId];
      try { sessionStorage.setItem('_rpt_dismissed', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // ── Flash highlight: detect Realtime-updated tour_day rows ───────────────
  const [recentlyChanged, setRecentlyChanged] = useState(new Set());
  const prevTourDaysRef = useRef(tourDays);
  useEffect(() => {
    const prev = prevTourDaysRef.current;
    prevTourDaysRef.current = tourDays;
    if (!memberRole || prev === tourDays) return;
    const changed = new Set();
    tourDays.forEach(d => {
      const p = prev.find(x => x.id === d.id);
      if (p && (p.bus_call !== d.bus_call || p.type !== d.type || p.notes !== d.notes)) changed.add(d.id);
    });
    if (!changed.size) return;
    setRecentlyChanged(s => new Set([...s, ...changed]));
    changed.forEach(id => setTimeout(() =>
      setRecentlyChanged(s => { const n = new Set(s); n.delete(id); return n; }), 2000));
  }, [tourDays]);

  // ── Pull-to-refresh ───────────────────────────────────────────────────────
  const [refreshing, setRefreshing]   = useState(false);
  const [pullProgress, setPullProgress] = useState(0); // 0 → 1
  const pullStartY = useRef(null);
  const pullArmed  = useRef(false); // only arm when the touched scroll view is at top

  // Walk up from the touch target to the nearest scrollable ancestor and read
  // its scrollTop. Pull-to-refresh should only engage when that view is at the
  // very top — otherwise scrolling content back up spuriously triggers refresh.
  const scrollTopOfTouch = (el) => {
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) return el.scrollTop;
      el = el.parentElement;
    }
    return 0;
  };

  const handleTouchStart = (e) => {
    pullStartY.current = e.touches[0].clientY;
    pullArmed.current  = scrollTopOfTouch(e.target) <= 0;
  };
  const handleTouchMove = (e) => {
    if (refreshing || pullStartY.current === null || !pullArmed.current) return;
    const delta = e.touches[0].clientY - pullStartY.current;
    if (delta > 0) setPullProgress(Math.min(delta / 72, 1));
  };
  const handleTouchEnd = async () => {
    if (pullProgress >= 1 && !refreshing && onRefresh) {
      setPullProgress(0);
      setRefreshing(true);
      try { await onRefresh(); } catch {}
      setRefreshing(false);
    } else {
      setPullProgress(0);
    }
    pullStartY.current = null;
  };

  // Close all overlays whenever a nav tab is selected
  const handleTab = (newTab) => {
    userPickedTab.current = true;
    setTab(newTab);
    setShowSelector(false);
    setShowDetail(false);
    setSelectedDay(null);
  };

  const handleSelectTour = (artistId, tourId) => {
    userPickedTab.current = true;
    setSelA(artistId);
    setSelT(tourId);
    setShowSelector(false);
    setTab("tour");
  };

  const renderScreen = () => {
    switch (tab) {
      case "today":
        return <TodayScreen
          tourDays={tourDays} tour={tour} venues={venues} dayWeather={dayWeather}
          memberRole={memberRole} memberModules={memberModules} lightMode={lightMode} UI={UI}
          dayLodging={dayLodging} dayFlights={dayFlights} dayTransport={dayTransport}
          loadDayLodging={loadDayLodging} loadDayFlights={loadDayFlights} loadDayTransport={loadDayTransport}
          loadDayWeather={loadDayWeather}
          memberOwnProfile={memberOwnProfile}
          memberNetworkProfiles={memberNetworkProfiles || []}
          memberConnections={memberConnections || new Set()}
          toggleMemberConnection={toggleMemberConnection}
          onGoToProfile={() => setTab("people")}
          cityRecs={cityRecs}
          loadCommunityRecs={loadCommunityRecs}
          dayScheduleItems={dayScheduleItems}
          showReports={showReports}
          setShowReportModal={setShowReportModal}
          reportDismissedShows={reportDismissedShows}
          dismissReport={dismissReport}
          broadcasts={broadcasts}
          broadcastReads={broadcastReads}
          markBroadcastRead={markBroadcastRead}
          currentUserId={user?.id}
        />;
      // "shows" tab removed — show detail now accessed via Tour tab DayDetailOverlay
      case "tour":
        return <TourScreen tourDays={tourDays} tour={tour} artist={artist} venues={venues} lightMode={lightMode} UI={UI} memberRole={memberRole}
          dayLodging={dayLodging} dayFlights={dayFlights} dayTransport={dayTransport}
          loadDayLodging={loadDayLodging} loadDayFlights={loadDayFlights} loadDayTransport={loadDayTransport}
          recentlyChanged={recentlyChanged}
          onDaySelect={setSelectedDay} />;
      case "schedule":
        return <ScheduleScreen
          tourDays={tourDays} tour={tour} venues={venues}
          dayScheduleItems={dayScheduleItems}
          profiles={profiles}
          memberRole={memberRole} memberOwnProfile={memberOwnProfile}
          lightMode={lightMode} UI={UI}
        />;
      case "people":
        return memberRole ? (
          <MemberProfileScreen
            memberOwnProfile={memberOwnProfile}
            memberNetworkProfiles={memberNetworkProfiles || []}
            memberConnections={memberConnections || new Set()}
            toggleMemberConnection={toggleMemberConnection}
            tourDays={tourDays} venues={venues} tour={tour}
            memberRole={memberRole} saveMemberProfile={saveMemberProfile}
            lightMode={lightMode} UI={UI}
          />
        ) : (
          <PeopleScreen
            profiles={profiles} tour={tour} artist={artist}
            teamId={teamId} currentUser={user}
            memberRole={memberRole} lightMode={lightMode} UI={UI} />
        );
      case "more":
        return (
          <MoreScreen
            artist={artist} tour={tour} lightMode={lightMode} UI={UI}
            onSwitchTour={() => setShowSelector(true)}
            saved={saved} memberRole={memberRole}
            onToggleTheme={() => setLightMode(p => {
              const next = !p;
              try { localStorage.setItem("lightMode", String(next)); } catch {}
              return next;
            })}
            onSignOut={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      onTouchStart={onRefresh ? handleTouchStart : undefined}
      onTouchMove={onRefresh ? handleTouchMove : undefined}
      onTouchEnd={onRefresh ? handleTouchEnd : undefined}
      style={{
        display: "flex", flexDirection: "column",
        height: "100dvh", background: UI.bg, overflow: "hidden",
        position: "fixed", top: 0, bottom: 0, left: 0, right: 0,
        // When shown to band/crew on a wide (desktop) viewport, cap to a phone
        // column and centre it. transform:translateZ(0) makes the inner
        // position:fixed children (bottom nav, overlays) anchor to THIS column
        // instead of the full viewport, so nothing breaks out of the frame.
        ...(constrainWidth ? {
          maxWidth: 480, margin: "0 auto",
          transform: "translateZ(0)",
          borderLeft: `1px solid ${UI.panelBdr}`,
          borderRight: `1px solid ${UI.panelBdr}`,
          // Fill the desktop gutters around the phone column with a dim backdrop.
          boxShadow: `0 0 0 100vmax ${lightMode ? "rgba(0,0,0,0.16)" : "rgba(0,0,0,0.55)"}`,
        } : {}),
      }}>
      {/* Top bar — zIndex 500 keeps it above the DayDetailOverlay (400) */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px",
        paddingTop: "env(safe-area-inset-top, 0px)",
        minHeight: "calc(48px + env(safe-area-inset-top, 0px))",
        background: UI.bg, borderBottom: `1px solid ${UI.panelBdr}`,
        flexShrink: 0,
        position: "relative", zIndex: 500,
      }}>
        {memberRole ? (
          /* Band/crew — static header showing tour context */
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 11, letterSpacing: "3px", textTransform: "uppercase", color: UI.amber, fontWeight: 700 }}>ADVANCD</div>
            {tour && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>{artist?.name} · {tour.name}</div>}
          </div>
        ) : (
          /* TM — tappable to switch tour */
          <button onClick={() => setShowSelector(true)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", WebkitTapHighlightColor: "transparent" }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 11, letterSpacing: "3px", textTransform: "uppercase", color: UI.amber, fontWeight: 700 }}>ADVANCD</div>
            {tour
              ? <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 1 }}>{artist?.name} · {tour.name}</div>
              : <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 1 }}>Tap to select a tour</div>
            }
          </button>
        )}
        {/* Right side: role badge + live indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {memberRole && (
            <span style={{
              fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
              color: memberRole === "band" ? UI.amber : UI.textFnt,
              background: memberRole === "band" ? UI.amdBg : UI.panelAlt,
              border: `1px solid ${memberRole === "band" ? UI.amdBdr : UI.panelBdr}`,
              padding: "3px 8px", borderRadius: 4,
            }}>{memberRole === "band" ? "Band" : "Crew"}</span>
          )}
          {/* Refresh spinner (pull-to-refresh active) */}
          {(refreshing || pullProgress > 0) && (
            <span style={{
              fontFamily: FF, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase",
              color: UI.amber, display: "flex", alignItems: "center", gap: 4,
            }}>
              {refreshing ? (
                <span style={{ animation: "spin 0.8s linear infinite", display: "inline-block", fontSize: 14 }}>↻</span>
              ) : (
                <span style={{ opacity: pullProgress }}>{pullProgress >= 1 ? "↻" : "↓"}</span>
              )}
              {refreshing ? "" : ""}
            </span>
          )}
          {/* Live indicator dot */}
          <span title={
            realtimeStatus === 'connected' ? "Live — updates in real time"
            : realtimeStatus === 'connecting' ? "Connecting…"
            : "Offline"
          } style={{
            display: "flex", alignItems: "center", gap: 4,
            fontFamily: FF, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase",
            color: realtimeStatus === 'connected' ? "var(--success)"
                 : realtimeStatus === 'connecting' ? "var(--accent)"
                 : "#555",
            transition: "color 0.4s",
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
              background: realtimeStatus === 'connected' ? "var(--success)"
                        : realtimeStatus === 'connecting' ? "var(--accent)"
                        : "#333",
              boxShadow: realtimeStatus === 'connected' ? "0 0 5px var(--success)" : "none",
              transition: "background 0.4s, box-shadow 0.4s",
            }} />
            {realtimeStatus === 'connected' ? "Live"
              : realtimeStatus === 'connecting' ? "…"
              : ""}
          </span>
        </div>
      </div>

      {/* Cached data banner — shown while live fetch hasn't completed yet */}
      {memberDataStale && memberRole && (
        <div style={{
          background: lightMode ? "#fffbe8" : "#1a1600",
          borderBottom: `1px solid ${UI.amdBdr}`,
          padding: "5px 20px",
          display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
        }}>
          <span style={{ fontSize: 11 }}>⏱</span>
          <span style={{ fontFamily: FF, fontSize: 11, color: UI.amber, letterSpacing: "0.3px" }}>
            Showing cached data — refreshing…
          </span>
        </div>
      )}

      {/* Screen content */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {renderScreen()}
      </div>

      {/* Bottom nav */}
      <BottomNav tab={tab} onTab={handleTab} UI={UI} memberRole={memberRole} />

      {/* AI Assistant — full-screen chat + voice dictation. Works for both
          members and TMs on mobile; the server resolves role + permissions
          from the JWT, so no gating is needed here. */}
      <ChatPanel
        mobile
        context={{
          tourId: tour?.id || selT || null,
          tourName: tour?.name || null,
          artistName: artist?.name || null,
          dateRange: tourDays.length > 0
            ? `${tourDays[0].date} to ${tourDays[tourDays.length - 1].date}`
            : null,
          // Screen awareness — "this day" resolves to the open day overlay
          screen: selectedDay
            ? { dayDate: selectedDay.date, tab }
            : { tab },
        }}
        onExecuteAction={onChatExecuteAction || undefined}
      />

      {/* Day detail overlay — rendered here (not inside TourScreen) so it sits above the top bar */}
      {selectedDay !== null && (
        <DayDetailOverlay
          tourDays={tourDays}
          initialIdx={tourDays.findIndex(d => d.id === selectedDay.id)}
          tour={tour} venues={venues} lightMode={lightMode} UI={UI} memberRole={memberRole}
          dayLodging={dayLodging} dayFlights={dayFlights} dayTransport={dayTransport}
          loadDayLodging={loadDayLodging} loadDayFlights={loadDayFlights} loadDayTransport={loadDayTransport}
          onClose={() => setSelectedDay(null)}
          user={user} teamId={teamId} memberOwnProfile={memberOwnProfile}
          memberPermissions={memberPermissions}
          memberNetworkProfiles={memberNetworkProfiles || []}
          memberConnections={memberConnections || new Set()}
          toggleMemberConnection={toggleMemberConnection}
          dayScheduleItems={dayScheduleItems}
          showSetlists={showSetlists}
          saveShowSetlist={saveShowSetlist}
          deleteShowSetlist={deleteShowSetlist}
          showReports={showReports}
          setShowReportModal={setShowReportModal}
        />
      )}

      {/* SR-1: Show report modal */}
      {showReportModal && (
        <ShowReportModal
          show={showReportModal.show}
          tourId={showReportModal.tourId}
          profileId={showReportModal.profileId}
          roleCategory={showReportModal.roleCategory}
          existingReport={showReportModal.existingReport}
          onSave={async (args) => {
            const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
            try {
              let teamIdForRow = null;
              if (args.profileId) {
                const { data: pd } = await supabase.from('people_profiles').select('team_id').eq('id', args.profileId).maybeSingle();
                teamIdForRow = pd?.team_id || null;
              }
              const { data: existing } = await supabase.from('show_reports')
                .select('id').eq('show_id', args.showId).eq('profile_id', args.profileId).maybeSingle();
              if (existing?.id) {
                const { error } = await supabase.from('show_reports')
                  .update({ responses: args.responses, submitted_at: new Date().toISOString() }).eq('id', existing.id);
                if (error) { console.error('[ADVANCD] update report:', error); return false; }
              } else {
                const { error } = await supabase.from('show_reports').insert({
                  id: genId(), show_id: args.showId, tour_id: args.tourId,
                  team_id: teamIdForRow, profile_id: args.profileId,
                  role_category: args.roleCategory, responses: args.responses,
                  submitted_at: new Date().toISOString(),
                });
                if (error) { console.error('[ADVANCD] insert report:', error); return false; }
              }
              return true;
            } catch (e) { console.error('[ADVANCD] saveReport mobile:', e); return false; }
          }}
          onClose={() => setShowReportModal(null)}
          lightMode={lightMode}
        />
      )}

      {/* Tour selector overlay */}
      {showSelector && (
        <TourSelectorOverlay
          artists={artists} selA={selA} selT={selT}
          onSelect={handleSelectTour}
          onClose={() => setShowSelector(false)}
          lightMode={lightMode} UI={UI}
        />
      )}

      {/* Show detail overlay removed — all show info now in DayDetailOverlay via Tour tab */}
    </div>
  );
}
